import { lazy, Suspense, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { UserManager, type User } from 'oidc-client-ts';
import type { CognitoUserSession } from 'amazon-cognito-identity-js';

import type {
  ApplicationInvoice,
  AuditFinding,
  AuditFindingClassification,
  AuditFindingStatus,
  BillingData,
  CurrentUser,
  FileRecord,
  LegalHold,
  LegalHoldScope,
  Membership,
  PlanAction,
  PlanActionStatus,
  PlanApprovalStatus,
  QualityItem,
  QualityItemKind,
  QualityItemStatus,
  QualitySummary,
  RetentionDraft,
  ScheduleCadence,
  ScheduleOccurrence,
  ScheduleOccurrenceSkipReason,
  ScheduleOccurrenceStatus,
  SupportCase,
  SurveyGuestToken,
  SurveyResponseRecord,
  SurveyTemplate,
  Tenant,
  TenantRetentionPolicy,
} from './domain-types';
import {
  createCognitoUserPool,
  currentCognitoSession,
  userFromCognitoSession,
} from './hooks/use-session';
import { useBilling } from './hooks/use-billing';
import { useFiles } from './hooks/use-files';
import { usePlatform } from './hooks/use-platform';
import { useSurveys } from './hooks/use-surveys';
import { useTenant } from './hooks/use-tenant';
import { useWorkflows } from './hooks/use-workflows';
import { LoginPanel } from './panels/login-panel';
import { AccountSecurityPanel, TenantMfaPolicyPanel } from './panels/account-security-panel';
import { ProfileSettings } from './panels/profile-settings';
import { OrganisationPage } from './panels/organisation-page';
import { OrganisationDirectory, PlatformPeopleDirectory } from './panels/administration-directory';
import { PurchasesPanel } from './panels/purchases-panel';
import { ProviderOnboarding } from './panels/onboarding-panel';
import { BillingPanel } from './panels/billing-panel';
import { FilesPanel } from './panels/files-panel';
import {
  AuditHistoryPanel,
  PlatformAdminPanel,
  PlatformStatusPanel,
} from './panels/platform-panel';
import { SurveyPanel } from './panels/survey-panel';
import { SupportPanel } from './panels/support-panel';
import {
  OrganisationStructurePanel,
  TenantMembersPanel,
  type MemberSecuritySummary,
} from './panels/tenant-panel';
import { WorkflowModulePanel, workflowModules as modules } from './panels/workflow-panel';
import { FloatingActions } from './components/floating-actions';
import { AnnouncementModalLayer } from './components/announcement-modal-layer';
import { AppUtilityBar } from './components/app-utility-bar';
import { AdminMenu, type AdminAreaId } from './components/admin-menu';
import { MessagesPage } from './panels/messages-panel';
import { NotificationsPage } from './panels/notifications-panel';
import { UserMenu } from './components/user-menu';
import { useProfilePreferences } from './profile-preferences';
import { recordAppActivity } from './preferences-api';
import { useThemePreference } from './theme';
import { organisationDisplayName } from './utils/organisation-display';

const AnnouncementsAdminPanel = lazy(() =>
  import('./panels/announcements-admin-panel').then((module) => ({
    default: module.AnnouncementsAdminPanel,
  })),
);
const TasksTimeLoggingPanel = lazy(() =>
  import('./panels/tasks-time-logging-panel').then((module) => ({
    default: module.TasksTimeLoggingPanel,
  })),
);

const legacyTokenCallbackRejected = (() => {
  if (window.location.pathname !== '/auth/callback') return false;
  if (!/(?:^|[&#])(?:id_token|access_token)=/.test(window.location.hash)) return false;
  window.history.replaceState({}, '', '/auth/callback');
  return true;
})();

const authCallbackUrl = (() => {
  if (window.location.pathname !== '/auth/callback') return '';
  const parameters = new URLSearchParams(window.location.search);
  if (!parameters.has('code') && !parameters.has('error')) return '';
  const callbackUrl = window.location.href;
  window.history.replaceState({}, '', '/auth/callback');
  return callbackUrl;
})();

type LegacyWorkspacePage = 'admin-tasks' | 'platform-admin-title';
type AdminWorkspacePage = AdminAreaId | LegacyWorkspacePage;
type WorkspacePage =
  | 'home'
  | 'profile'
  | 'organisation'
  | 'purchases'
  | 'messages'
  | 'notifications'
  | AdminWorkspacePage;

const workspacePages = new Set<WorkspacePage>([
  'profile',
  'organisation',
  'purchases',
  'messages',
  'notifications',
  'platform-admin-title',
  'admin-announcements',
  'admin-organisations',
  'admin-people',
  'admin-tasks',
]);

function initialWorkspacePage(): WorkspacePage {
  const page = window.location.hash.slice(1).split('/')[0] as WorkspacePage;
  return workspacePages.has(page) ? page : 'home';
}

function isAdminWorkspacePage(page: WorkspacePage): page is AdminWorkspacePage {
  return (
    page === 'platform-admin-title' ||
    page === 'admin-announcements' ||
    page === 'admin-organisations' ||
    page === 'admin-people' ||
    page === 'admin-tasks'
  );
}

function profileClaim(profile: User['profile'], claim: 'name' | 'picture'): string {
  const value = profile[claim];
  return typeof value === 'string' ? value : '';
}

function safeProfileImage(profile: User['profile']): string {
  const picture = profileClaim(profile, 'picture');
  try {
    return new URL(picture).protocol === 'https:' ? picture : '';
  } catch {
    return '';
  }
}

function csvCell(value: string): string {
  const spreadsheetSafe = /^[=+\-@]/.test(value) ? `'${value}` : value;
  return `"${spreadsheetSafe.replaceAll('"', '""')}"`;
}

function downloadTextFile(contents: string, fileName: string, contentType: string) {
  const blob = new Blob([contents], { type: contentType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

function safeFileName(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);
}

function membershipHasRole(roles: string[] | undefined, role: string): boolean {
  const equivalentRoles: Record<string, string[]> = {
    standard_user: ['standard_user', 'viewer'],
    manager: ['manager', 'quality_manager'],
  };
  return (equivalentRoles[role] ?? [role]).some((candidate) => roles?.includes(candidate));
}

const permittedStatusTransitions: Record<QualityItemStatus, QualityItemStatus[]> = {
  draft: ['scheduled', 'in_progress', 'cancelled'],
  scheduled: ['in_progress', 'cancelled'],
  in_progress: ['complete', 'cancelled'],
  complete: [],
  cancelled: [],
};

const statusLabels: Record<QualityItemStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  in_progress: 'In progress',
  complete: 'Complete',
  cancelled: 'Cancelled',
};

function availableQualityItemTransitions(item: QualityItem): QualityItemStatus[] {
  if (
    item.kind === 'plan' &&
    item.status === 'draft' &&
    (item.planApproval?.status ?? 'not_submitted') !== 'approved'
  ) {
    return [];
  }
  return permittedStatusTransitions[item.status];
}

const permittedPlanActionTransitions: Record<PlanActionStatus, PlanActionStatus[]> = {
  open: ['in_progress', 'cancelled'],
  in_progress: ['complete', 'cancelled'],
  complete: [],
  cancelled: [],
};

const planActionStatusLabels: Record<PlanActionStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  complete: 'Complete',
  cancelled: 'Cancelled',
};

const permittedAuditFindingTransitions: Record<AuditFindingStatus, AuditFindingStatus[]> = {
  open: ['in_progress', 'closed'],
  in_progress: ['closed'],
  closed: [],
};

const auditFindingStatusLabels: Record<AuditFindingStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  closed: 'Closed',
};

const auditFindingClassificationLabels: Record<AuditFindingClassification, string> = {
  conformity: 'Conformity',
  observation: 'Observation',
  minor_nonconformity: 'Minor nonconformity',
  major_nonconformity: 'Major nonconformity',
};

const permittedScheduleOccurrenceTransitions: Record<
  ScheduleOccurrenceStatus,
  ScheduleOccurrenceStatus[]
> = {
  pending: ['in_progress', 'skipped'],
  in_progress: ['complete', 'skipped'],
  complete: [],
  skipped: [],
};

const scheduleOccurrenceStatusLabels: Record<ScheduleOccurrenceStatus, string> = {
  pending: 'Pending',
  in_progress: 'In progress',
  complete: 'Complete',
  skipped: 'Skipped',
};

const planApprovalStatusLabels: Record<PlanApprovalStatus, string> = {
  not_submitted: 'Not submitted',
  pending: 'Pending approval',
  approved: 'Approved',
  changes_requested: 'Changes requested',
};

const scheduleOccurrenceSkipReasonLabels: Record<ScheduleOccurrenceSkipReason, string> = {
  not_required: 'Not required',
  duplicate: 'Duplicate',
  superseded: 'Superseded',
  unavailable: 'Required people or evidence unavailable',
  other: 'Other',
};

function groupScheduleOccurrences(
  occurrences: ScheduleOccurrence[],
  view: 'agenda' | 'calendar',
): Array<{ label: string; items: ScheduleOccurrence[] }> {
  if (view === 'agenda') return [{ label: '', items: occurrences }];
  const groups = new Map<string, ScheduleOccurrence[]>();
  for (const occurrence of occurrences) {
    const label = new Date(occurrence.scheduledFor).toLocaleDateString('en-AU', {
      timeZone: 'Australia/Sydney',
      month: 'long',
      year: 'numeric',
    });
    groups.set(label, [...(groups.get(label) ?? []), occurrence]);
  }
  return [...groups].map(([label, items]) => ({ label, items }));
}

function isOccurrenceOverdue(occurrence: ScheduleOccurrence): boolean {
  return (
    (occurrence.status === 'pending' || occurrence.status === 'in_progress') &&
    new Date(occurrence.scheduledFor).getTime() < Date.now()
  );
}

export const SurveyPage = SurveyPanel;

export function ProviderVerificationPage({ apiUrl }: { apiUrl: string }) {
  const token = window.location.hash.slice(1);
  const [preview, setPreview] = useState<{
    providerCode: string;
    providerName: string;
    claimantEmail: string;
    expiresAt: string;
  } | null>(null);
  const [message, setMessage] = useState('Checking this verification link…');
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await fetch(`${apiUrl}/v1/provider-verifications/preview`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        if (!response.ok) {
          setMessage('This verification link is invalid, expired or has already been used.');
          return;
        }
        setPreview(
          (await response.json()) as {
            providerCode: string;
            providerName: string;
            claimantEmail: string;
            expiresAt: string;
          },
        );
        setMessage('');
      } catch {
        setMessage('The verification service is temporarily unavailable. Please try again.');
      }
    };
    void load();
  }, [apiUrl, token]);

  const confirm = async () => {
    setConfirming(true);
    const response = await fetch(`${apiUrl}/v1/provider-verifications/confirm`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    setConfirming(false);
    if (!response.ok) {
      setMessage('Verification could not be completed. The link may have expired or been used.');
      return;
    }
    setPreview(null);
    setMessage('The provider account is verified. You may close this page.');
    window.history.replaceState({}, '', '/provider-verification');
  };

  return (
    <main className="provider-onboarding-page">
      <a className="skip-link" href="#provider-verification-content">
        Skip to verification content
      </a>
      <section
        className="provider-onboarding-card"
        id="provider-verification-content"
        tabIndex={-1}
      >
        <a className="brand" href="/">
          quality<span>.vet</span>
        </a>
        <p className="eyebrow">Provider verification</p>
        <h1>Confirm this RTO account</h1>
        {preview ? (
          <>
            <dl className="provider-preview">
              <div>
                <dt>Provider</dt>
                <dd>{preview.providerName}</dd>
              </div>
              <div>
                <dt>Registration code</dt>
                <dd>{preview.providerCode}</dd>
              </div>
              <div>
                <dt>Account claimant</dt>
                <dd>{preview.claimantEmail}</dd>
              </div>
              <div>
                <dt>Link expires</dt>
                <dd>{new Date(preview.expiresAt).toLocaleString()}</dd>
              </div>
            </dl>
            <p>
              Confirm only if this person is authorised to establish the provider’s quality.vet
              account. Opening this page has not verified anything.
            </p>
            <button disabled={confirming} type="button" onClick={() => void confirm()}>
              {confirming ? 'Confirming…' : 'Confirm provider account'}
            </button>
          </>
        ) : (
          <p aria-atomic="true" role="status" aria-live="polite">
            {message}
          </p>
        )}
      </section>
    </main>
  );
}

export function App() {
  const config = window.QUALITY_VET_CONFIG;
  const authConfigured = Boolean(config.cognitoAuthority && config.cognitoClientId);
  const emailMfaAvailable = config.emailMfaAvailable === true;
  const manager = useMemo(
    () =>
      authConfigured
        ? new UserManager({
            authority: config.cognitoAuthority,
            client_id: config.cognitoClientId,
            redirect_uri: config.redirectUri,
            post_logout_redirect_uri: config.logoutUri,
            response_type: 'code',
            scope: 'openid email profile',
            disablePKCE: false,
          })
        : undefined,
    [authConfigured, config],
  );
  const pool = useMemo(
    () => (authConfigured ? createCognitoUserPool(config) : undefined),
    [authConfigured, config],
  );
  const [user, setUser] = useState<User | null>(null);
  const userKey =
    typeof user?.profile.sub === 'string'
      ? user.profile.sub
      : typeof user?.profile.email === 'string'
        ? user.profile.email
        : undefined;
  const preferenceSync = useMemo(
    () => ({
      apiUrl: config.apiUrl,
      ...(user?.access_token ? { accessToken: user.access_token } : {}),
    }),
    [config.apiUrl, user?.access_token],
  );
  const { theme, setTheme } = useThemePreference(userKey, preferenceSync);
  const { profile, setProfile } = useProfilePreferences(
    userKey,
    {
      firstName: user
        ? (() => {
            const name =
              profileClaim(user.profile, 'name') ||
              (typeof user.profile.email === 'string' ? user.profile.email : 'Account');
            const parts = name.trim().split(/\s+/);
            return parts[0] ?? 'Account';
          })()
        : 'Account',
      familyName: user
        ? (() => {
            const name = profileClaim(user.profile, 'name') || '';
            const parts = name.trim().split(/\s+/);
            return parts.slice(1).join(' ');
          })()
        : '',
      phone: '',
      bio: '',
      pictureUrl: user ? safeProfileImage(user.profile) : '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Australia/Sydney',
    },
    preferenceSync,
  );
  const [workspacePage, setWorkspacePage] = useState<WorkspacePage>(initialWorkspacePage);
  const [initialNotificationId, setInitialNotificationId] = useState<string | undefined>();
  const [initialConversationId, setInitialConversationId] = useState<string | undefined>();
  const [utilityBarRefreshKey, setUtilityBarRefreshKey] = useState(0);
  const legacyLandingPageRemoved = true;
  const [authInitialising, setAuthInitialising] = useState(authConfigured);
  const [accountContextReady, setAccountContextReady] = useState(false);
  const [platformTenantsReady, setPlatformTenantsReady] = useState(false);
  const [tenantLoading, setTenantLoading] = useState(false);
  const [authError, setAuthError] = useState(
    legacyTokenCallbackRejected
      ? 'For your security, legacy token links are not accepted. Please log in again.'
      : '',
  );
  const [tenantMfaBusy, setTenantMfaBusy] = useState(false);
  const [organisationStructureBusy, setOrganisationStructureBusy] = useState(false);
  const [roleMigrationBusy, setRoleMigrationBusy] = useState(false);
  const [roleMigrationMessage, setRoleMigrationMessage] = useState('');
  const [memberSecuritySummaries, setMemberSecuritySummaries] = useState<MemberSecuritySummary[]>(
    [],
  );
  const memberLoadGeneration = useRef(0);
  const {
    currentUser,
    setCurrentUser,
    selectedTenantId,
    setSelectedTenantId,
    tenant,
    setTenant,
    members,
    setMembers,
    inviteEmail,
    setInviteEmail,
    inviteRole,
    setInviteRole,
    notificationEmail,
    setNotificationEmail,
    offboardingReason,
    setOffboardingReason,
    supportCases,
    setSupportCases,
    supportSubject,
    setSupportSubject,
    supportDescription,
    setSupportDescription,
    supportPriority,
    setSupportPriority,
    supportResponses,
    setSupportResponses,
    supportBusy,
    setSupportBusy,
    retentionPolicy,
    setRetentionPolicy,
    legalHolds,
    setLegalHolds,
    retentionDraft,
    setRetentionDraft,
    legalHoldScope,
    setLegalHoldScope,
    legalHoldReason,
    setLegalHoldReason,
    legalHoldReference,
    setLegalHoldReference,
    legalHoldReleaseReasons,
    setLegalHoldReleaseReasons,
    retentionBusy,
    setRetentionBusy,
  } = useTenant();
  const {
    activeKind,
    setActiveKind,
    qualityItems,
    setQualityItems,
    qualitySummary,
    setQualitySummary,
    qualityHorizonDays,
    setQualityHorizonDays,
    itemTitle,
    setItemTitle,
    itemDescription,
    setItemDescription,
    itemDueAt,
    setItemDueAt,
    planObligation,
    setPlanObligation,
    planObjective,
    setPlanObjective,
    planSuccessMeasures,
    setPlanSuccessMeasures,
    scheduleActivity,
    setScheduleActivity,
    scheduleCadence,
    setScheduleCadence,
    auditScope,
    setAuditScope,
    auditCriteria,
    setAuditCriteria,
    auditSampling,
    setAuditSampling,
    completionSummaries,
    setCompletionSummaries,
    planApprovalComments,
    setPlanApprovalComments,
    planReopenReasons,
    setPlanReopenReasons,
    expandedPlanId,
    setExpandedPlanId,
    planActions,
    setPlanActions,
    planActionDescription,
    setPlanActionDescription,
    planActionDueAt,
    setPlanActionDueAt,
    planActionEvidenceFileId,
    setPlanActionEvidenceFileId,
    planActionCompletionSummaries,
    setPlanActionCompletionSummaries,
    expandedAuditId,
    setExpandedAuditId,
    auditFindings,
    setAuditFindings,
    auditFindingTitle,
    setAuditFindingTitle,
    auditFindingStatement,
    setAuditFindingStatement,
    auditFindingClassification,
    setAuditFindingClassification,
    auditFindingCorrectiveAction,
    setAuditFindingCorrectiveAction,
    auditFindingDueAt,
    setAuditFindingDueAt,
    auditFindingEvidenceFileId,
    setAuditFindingEvidenceFileId,
    auditFindingClosureSummaries,
    setAuditFindingClosureSummaries,
    auditFindingClosureEvidence,
    setAuditFindingClosureEvidence,
    expandedScheduleId,
    setExpandedScheduleId,
    scheduleOccurrences,
    setScheduleOccurrences,
    scheduleOccurrenceView,
    setScheduleOccurrenceView,
    scheduleOccurrenceSummaries,
    setScheduleOccurrenceSummaries,
    scheduleOccurrenceReschedules,
    setScheduleOccurrenceReschedules,
    scheduleOccurrenceSkipReasons,
    setScheduleOccurrenceSkipReasons,
    scheduleOccurrenceSkipDetails,
    setScheduleOccurrenceSkipDetails,
    savingItem,
    setSavingItem,
  } = useWorkflows();
  const {
    surveyAudience,
    setSurveyAudience,
    surveyConsentStatement,
    setSurveyConsentStatement,
    surveyQuestions,
    setSurveyQuestions,
    surveyTemplates,
    setSurveyTemplates,
    surveyTemplateName,
    setSurveyTemplateName,
    surveyShareLink,
    setSurveyShareLink,
    surveyShareToken,
    setSurveyShareToken,
    surveyLinks,
    setSurveyLinks,
    surveyResults,
    setSurveyResults,
  } = useSurveys();
  const { files, setFiles, selectedFile, setSelectedFile, uploadingFile, setUploadingFile } =
    useFiles();
  const {
    billingData,
    setBillingData,
    billingContactName,
    setBillingContactName,
    billingEmail,
    setBillingEmail,
    billingAddress,
    setBillingAddress,
    billingBusy,
    setBillingBusy,
    giftDurationDays,
    setGiftDurationDays,
    giftReason,
    setGiftReason,
  } = useBilling();
  const {
    auditEvents,
    platformTenants,
    setPlatformTenants,
    newTenantName,
    setNewTenantName,
    newTenantSlug,
    setNewTenantSlug,
    newTenantAdministratorEmail,
    setNewTenantAdministratorEmail,
    tenantLifecycleBusy,
    setTenantLifecycleBusy,
    qualityReportBusy,
    setQualityReportBusy,
  } = usePlatform();
  const selectedMembership = currentUser?.memberships.find(
    (membership) => membership.tenantId === selectedTenantId && membership.status === 'active',
  );
  const selectedOrganisationName = organisationDisplayName(tenant?.name);
  const providerHighTrustEnabled =
    !tenant?.tgaProviderCode || tenant.providerVerificationStatus === 'verified';
  const canAdministerMembers = Boolean(
    providerHighTrustEnabled &&
    (currentUser?.isPlatformAdmin || membershipHasRole(selectedMembership?.roles, 'tenant_admin')),
  );
  const canManageAllPersonnelAssignments = Boolean(
    canAdministerMembers || membershipHasRole(selectedMembership?.roles, 'manager'),
  );
  const canManagePersonnel = Boolean(
    providerHighTrustEnabled &&
    (canManageAllPersonnelAssignments ||
      membershipHasRole(selectedMembership?.roles, 'team_leader')),
  );
  const mfaRequiredForSelectedTenant = Boolean(
    tenant?.mfaRequired || currentUser?.mfaRequiredTenantIds.includes(selectedTenantId),
  );
  const canManageRetention = canAdministerMembers;
  const canViewRetention = Boolean(
    canManageRetention || membershipHasRole(selectedMembership?.roles, 'manager'),
  );
  const hasSubscriptionAccess = Boolean(
    currentUser?.isPlatformAdmin ||
    (tenant?.tgaProviderCode && tenant.providerVerificationStatus !== 'verified') ||
    (tenant?.subscriptionAccessUntil &&
      new Date(tenant.subscriptionAccessUntil).getTime() > Date.now()),
  );
  const canManageKind = (kind: QualityItemKind) =>
    Boolean(
      currentUser?.isPlatformAdmin ||
      membershipHasRole(selectedMembership?.roles, 'tenant_admin') ||
      membershipHasRole(selectedMembership?.roles, 'manager') ||
      (kind === 'audit' && selectedMembership?.roles.includes('auditor')),
    );
  const canApprovePlans = Boolean(membershipHasRole(selectedMembership?.roles, 'tenant_admin'));
  const eligibleWorkflowOwners = members.filter(
    (membership) =>
      membership.status === 'active' &&
      (membershipHasRole(membership.roles, 'tenant_admin') ||
        membershipHasRole(membership.roles, 'manager')),
  );
  const canReviewSurveyResults = Boolean(
    currentUser?.isPlatformAdmin ||
    membershipHasRole(selectedMembership?.roles, 'tenant_admin') ||
    membershipHasRole(selectedMembership?.roles, 'manager') ||
    selectedMembership?.roles.includes('auditor'),
  );
  const workspaceContextInitialising = Boolean(
    user?.access_token &&
    (!accountContextReady ||
      (currentUser &&
        ((currentUser.isPlatformAdmin && !platformTenantsReady) ||
          (selectedTenantId && (tenantLoading || tenant?.id !== selectedTenantId))))),
  );

  useEffect(() => {
    if (!currentUser || !isAdminWorkspacePage(workspacePage) || currentUser.isPlatformAdmin) return;
    setWorkspacePage('home');
    window.history.replaceState(null, '', '/');
  }, [currentUser, workspacePage]);

  useEffect(() => {
    const syncWorkspacePage = () => setWorkspacePage(initialWorkspacePage());
    window.addEventListener('hashchange', syncWorkspacePage);
    return () => window.removeEventListener('hashchange', syncWorkspacePage);
  }, []);

  useEffect(() => {
    if (!manager) return;
    const finishSignIn = async () => {
      try {
        if (authCallbackUrl) {
          await manager.signinRedirectCallback(authCallbackUrl);
          window.history.replaceState({}, '', '/');
        } else if (pool) {
          const session = await currentCognitoSession(pool);
          if (session) {
            const signedInUser = userFromCognitoSession(session);
            await manager.storeUser(signedInUser);
            setUser(signedInUser);
            return;
          }
        }
        const storedUser = await manager.getUser();
        if (storedUser?.expired) {
          await manager.removeUser();
          setUser(null);
        } else {
          setUser(storedUser);
        }
      } catch {
        setAuthError('Login could not be completed. Please try again or reset your password.');
      } finally {
        setAuthInitialising(false);
      }
    };
    void finishSignIn();
  }, [manager, pool]);

  useEffect(() => {
    if (!user?.access_token) {
      setCurrentUser(null);
      setAccountContextReady(false);
      setPlatformTenantsReady(false);
      return;
    }
    let active = true;
    setAccountContextReady(false);
    setPlatformTenantsReady(false);
    const loadMemberships = async () => {
      try {
        const response = await fetch(`${config.apiUrl}/v1/me`, {
          headers: { authorization: `Bearer ${user.access_token}` },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const profile = (await response.json()) as CurrentUser;
        if (!active) return;
        setCurrentUser(profile);
        const activeMembership = profile.memberships.find((item) => item.status === 'active');
        setSelectedTenantId((existing) => existing || activeMembership?.tenantId || '');
      } catch {
        if (active) {
          setAuthError('Your account loaded, but organisation access could not be retrieved.');
        }
      } finally {
        if (active) setAccountContextReady(true);
      }
    };
    void loadMemberships();
    return () => {
      active = false;
    };
  }, [config.apiUrl, user]);

  useEffect(() => {
    if (!user?.access_token) return;
    void recordAppActivity(preferenceSync).catch(() => undefined);
  }, [preferenceSync, user?.access_token]);

  const loadPlatformTenants = async () => {
    if (!user?.access_token || !currentUser?.isPlatformAdmin) {
      setPlatformTenants([]);
      return;
    }
    const response = await fetch(`${config.apiUrl}/v1/platform/tenants`, {
      headers: { authorization: `Bearer ${user.access_token}` },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const body = (await response.json()) as { items: Tenant[] };
    setPlatformTenants(body.items);
    setSelectedTenantId((existing) => existing || body.items[0]?.id || '');
  };

  useEffect(() => {
    let active = true;
    if (!currentUser?.isPlatformAdmin) {
      setPlatformTenantsReady(true);
      void loadPlatformTenants();
      return () => {
        active = false;
      };
    }
    setPlatformTenantsReady(false);
    void loadPlatformTenants()
      .catch(() => {
        if (active) setAuthError('Platform organisation administration could not be loaded.');
      })
      .finally(() => {
        if (active) setPlatformTenantsReady(true);
      });
    return () => {
      active = false;
    };
  }, [config.apiUrl, currentUser?.isPlatformAdmin, user?.access_token]);

  useEffect(() => {
    if (!user?.access_token || !selectedTenantId) {
      setTenant(null);
      setTenantLoading(false);
      return;
    }
    let active = true;
    setTenantLoading(true);
    setTenant(null);
    const loadTenant = async () => {
      const response = await fetch(`${config.apiUrl}/v1/tenants/${selectedTenantId}`, {
        headers: { authorization: `Bearer ${user.access_token}` },
      });
      const nextTenant = response.ok ? ((await response.json()) as Tenant) : null;
      if (active) setTenant(nextTenant);
    };
    void loadTenant()
      .catch(() => {
        if (active) setTenant(null);
      })
      .finally(() => {
        if (active) setTenantLoading(false);
      });
    return () => {
      active = false;
    };
  }, [config.apiUrl, selectedTenantId, user]);

  const loadQualityItems = async () => {
    if (!user?.access_token || !selectedTenantId || !hasSubscriptionAccess) {
      setQualityItems([]);
      return;
    }
    const response = await fetch(`${config.apiUrl}/v1/tenants/${selectedTenantId}/quality-items`, {
      headers: { authorization: `Bearer ${user.access_token}` },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const body = (await response.json()) as { items: QualityItem[] };
    setQualityItems(body.items);
  };

  useEffect(() => {
    if (legacyLandingPageRemoved) {
      setQualityItems([]);
      return;
    }
    void loadQualityItems().catch(() =>
      setAuthError('Quality records could not be loaded for this organisation.'),
    );
  }, [config.apiUrl, hasSubscriptionAccess, selectedTenantId, user?.access_token]);

  const loadSurveyTemplates = async () => {
    if (!user?.access_token || !selectedTenantId || !hasSubscriptionAccess) {
      setSurveyTemplates([]);
      return;
    }
    const response = await fetch(
      `${config.apiUrl}/v1/tenants/${selectedTenantId}/survey-templates`,
      { headers: { authorization: `Bearer ${user.access_token}` } },
    );
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const body = (await response.json()) as { items: SurveyTemplate[] };
    setSurveyTemplates(body.items);
  };

  useEffect(() => {
    if (legacyLandingPageRemoved) {
      setSurveyTemplates([]);
      return;
    }
    void loadSurveyTemplates().catch(() =>
      setAuthError('Survey templates could not be loaded for this organisation.'),
    );
  }, [config.apiUrl, hasSubscriptionAccess, selectedTenantId, user?.access_token]);

  useEffect(() => {
    if (legacyLandingPageRemoved) {
      setQualitySummary(null);
      return;
    }
    if (!user?.access_token || !selectedTenantId || !hasSubscriptionAccess) {
      setQualitySummary(null);
      return;
    }
    const loadQualitySummary = async () => {
      const response = await fetch(
        `${config.apiUrl}/v1/tenants/${selectedTenantId}/quality-summary?horizonDays=${qualityHorizonDays}`,
        { headers: { authorization: `Bearer ${user.access_token}` } },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setQualitySummary((await response.json()) as QualitySummary);
    };
    void loadQualitySummary().catch(() =>
      setAuthError('The organisation quality summary could not be loaded.'),
    );
  }, [
    config.apiUrl,
    hasSubscriptionAccess,
    selectedTenantId,
    user?.access_token,
    qualityItems.length,
    qualityHorizonDays,
  ]);

  const loadFiles = async () => {
    if (!user?.access_token || !selectedTenantId || !hasSubscriptionAccess) {
      setFiles([]);
      return;
    }
    const response = await fetch(`${config.apiUrl}/v1/tenants/${selectedTenantId}/files`, {
      headers: { authorization: `Bearer ${user.access_token}` },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const body = (await response.json()) as { items: FileRecord[] };
    setFiles(body.items);
  };

  useEffect(() => {
    if (legacyLandingPageRemoved) {
      setFiles([]);
      return;
    }
    void loadFiles().catch(() => setAuthError('Organisation files could not be loaded.'));
  }, [config.apiUrl, hasSubscriptionAccess, selectedTenantId, user?.access_token]);

  const loadMembers = async () => {
    const generation = ++memberLoadGeneration.current;
    setMembers([]);
    setMemberSecuritySummaries([]);
    if (!user?.access_token || !selectedTenantId || !canManagePersonnel) {
      return;
    }
    const response = await fetch(`${config.apiUrl}/v1/tenants/${selectedTenantId}/memberships`, {
      headers: { authorization: `Bearer ${user.access_token}` },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const body = (await response.json()) as { items: Membership[] };
    if (generation !== memberLoadGeneration.current) return;
    setMembers(body.items);
    if (!canAdministerMembers) {
      setMemberSecuritySummaries([]);
      return;
    }
    const securityResponse = await fetch(
      `${config.apiUrl}/v1/tenants/${selectedTenantId}/memberships/security-summary`,
      { headers: { authorization: `Bearer ${user.access_token}` } },
    );
    if (!securityResponse.ok) throw new Error(`HTTP ${securityResponse.status}`);
    const securityBody = (await securityResponse.json()) as { items: MemberSecuritySummary[] };
    if (generation !== memberLoadGeneration.current) return;
    setMemberSecuritySummaries(securityBody.items);
  };

  useEffect(() => {
    if (
      legacyLandingPageRemoved &&
      (workspacePage !== 'admin-people' || !currentUser?.isPlatformAdmin)
    ) {
      setMembers([]);
      setMemberSecuritySummaries([]);
      memberLoadGeneration.current += 1;
      return;
    }
    void loadMembers().catch(() => setAuthError('Organisation members could not be loaded.'));
  }, [
    canManagePersonnel,
    canAdministerMembers,
    config.apiUrl,
    currentUser?.isPlatformAdmin,
    selectedTenantId,
    user?.access_token,
    workspacePage,
  ]);

  const loadBilling = async () => {
    if (!user?.access_token || !selectedTenantId || !canAdministerMembers) {
      setBillingData(null);
      return;
    }
    const response = await fetch(`${config.apiUrl}/v1/tenants/${selectedTenantId}/billing`, {
      headers: { authorization: `Bearer ${user.access_token}` },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const body = (await response.json()) as BillingData;
    setBillingData(body);
    setBillingContactName(
      body.tenant.billingContactName ??
        organisationDisplayName(body.tenant.name) ??
        body.tenant.name,
    );
    setBillingEmail(body.tenant.billingEmail ?? currentUser?.email ?? '');
    setBillingAddress((body.tenant.billingAddressLines ?? []).join('\n'));
  };

  useEffect(() => {
    if (workspacePage !== 'purchases') {
      setBillingData(null);
      return;
    }
    void loadBilling().catch(() =>
      setAuthError('Organisation subscription and invoice details could not be loaded.'),
    );
  }, [
    canAdministerMembers,
    config.apiUrl,
    currentUser?.email,
    selectedTenantId,
    user?.access_token,
    workspacePage,
  ]);

  const loadSupportCases = async () => {
    if (!user?.access_token || !selectedTenantId) {
      setSupportCases([]);
      return;
    }
    const response = await fetch(`${config.apiUrl}/v1/tenants/${selectedTenantId}/support-cases`, {
      headers: { authorization: `Bearer ${user.access_token}` },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const body = (await response.json()) as { items: SupportCase[] };
    setSupportCases(body.items);
  };

  const loadRetention = async () => {
    if (!user?.access_token || !selectedTenantId || !canViewRetention) {
      setRetentionPolicy(null);
      setLegalHolds([]);
      return;
    }
    const response = await fetch(`${config.apiUrl}/v1/tenants/${selectedTenantId}/retention`, {
      headers: { authorization: `Bearer ${user.access_token}` },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const body = (await response.json()) as {
      policy?: TenantRetentionPolicy;
      holds: LegalHold[];
    };
    setRetentionPolicy(body.policy ?? null);
    setLegalHolds(body.holds);
    if (body.policy) {
      setRetentionDraft({
        qualityRecordsDays: body.policy.qualityRecordsDays,
        surveyResponsesDays: body.policy.surveyResponsesDays,
        filesDays: body.policy.filesDays,
        supportCasesDays: body.policy.supportCasesDays,
        auditEventsDays: body.policy.auditEventsDays,
      });
    }
  };

  useEffect(() => {
    if (legacyLandingPageRemoved) {
      setRetentionPolicy(null);
      setLegalHolds([]);
      return;
    }
    void loadRetention().catch(() =>
      setAuthError('Retention settings and legal holds could not be loaded.'),
    );
  }, [canViewRetention, config.apiUrl, selectedTenantId, user?.access_token]);

  const updateRetentionDraft = (field: keyof RetentionDraft, value: number) => {
    setRetentionDraft((current) => ({ ...current, [field]: value }));
  };

  const saveRetentionPolicy = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!user?.access_token || !selectedTenantId || !canManageRetention) return;
    setRetentionBusy(true);
    setAuthError('');
    try {
      const response = await fetch(
        `${config.apiUrl}/v1/tenants/${selectedTenantId}/retention-policy`,
        {
          method: 'PATCH',
          headers: {
            authorization: `Bearer ${user.access_token}`,
            'content-type': 'application/json',
          },
          body: JSON.stringify(retentionDraft),
        },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setRetentionPolicy((await response.json()) as TenantRetentionPolicy);
    } catch {
      setAuthError('The retention settings could not be saved.');
    } finally {
      setRetentionBusy(false);
    }
  };

  const createLegalHold = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (
      !user?.access_token ||
      !selectedTenantId ||
      !canManageRetention ||
      legalHoldReason.trim().length < 10
    ) {
      return;
    }
    setRetentionBusy(true);
    setAuthError('');
    try {
      const response = await fetch(`${config.apiUrl}/v1/tenants/${selectedTenantId}/legal-holds`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${user.access_token}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          scope: legalHoldScope,
          reason: legalHoldReason.trim(),
          ...(legalHoldReference.trim() ? { reference: legalHoldReference.trim() } : {}),
        }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setLegalHoldReason('');
      setLegalHoldReference('');
      await loadRetention();
    } catch {
      setAuthError('The legal hold could not be created.');
    } finally {
      setRetentionBusy(false);
    }
  };

  const releaseLegalHold = async (hold: LegalHold) => {
    const releaseReason = legalHoldReleaseReasons[hold.id]?.trim() ?? '';
    if (
      !user?.access_token ||
      !selectedTenantId ||
      !canManageRetention ||
      releaseReason.length < 10
    ) {
      return;
    }
    setRetentionBusy(true);
    setAuthError('');
    try {
      const response = await fetch(
        `${config.apiUrl}/v1/tenants/${selectedTenantId}/legal-holds/${hold.id}`,
        {
          method: 'PATCH',
          headers: {
            authorization: `Bearer ${user.access_token}`,
            'content-type': 'application/json',
          },
          body: JSON.stringify({ releaseReason }),
        },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setLegalHoldReleaseReasons((current) => {
        const next = { ...current };
        delete next[hold.id];
        return next;
      });
      await loadRetention();
    } catch {
      setAuthError('The legal hold could not be released.');
    } finally {
      setRetentionBusy(false);
    }
  };

  const exportAuditEvents = () => {
    const blob = new Blob([JSON.stringify(auditEvents, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `quality-vet-audit-${selectedTenantId}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportQualityReport = async () => {
    if (!user?.access_token || !selectedTenantId || !canReviewSurveyResults) return;
    setQualityReportBusy(true);
    setAuthError('');
    try {
      const response = await fetch(
        `${config.apiUrl}/v1/tenants/${selectedTenantId}/quality-report`,
        { headers: { authorization: `Bearer ${user.access_token}` } },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      downloadTextFile(
        await response.text(),
        `quality-vet-register-${safeFileName(tenant?.name ?? selectedTenantId)}-${new Date()
          .toISOString()
          .slice(0, 10)}.csv`,
        'text/csv;charset=utf-8',
      );
    } catch {
      setAuthError('The organisation quality report could not be exported.');
    } finally {
      setQualityReportBusy(false);
    }
  };

  const createSupportCase = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (
      !user?.access_token ||
      !selectedTenantId ||
      supportSubject.trim().length < 5 ||
      supportDescription.trim().length < 10
    ) {
      return;
    }
    setSupportBusy(true);
    setAuthError('');
    try {
      const response = await fetch(
        `${config.apiUrl}/v1/tenants/${selectedTenantId}/support-cases`,
        {
          method: 'POST',
          headers: {
            authorization: `Bearer ${user.access_token}`,
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            subject: supportSubject.trim(),
            description: supportDescription.trim(),
            priority: supportPriority,
          }),
        },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setSupportSubject('');
      setSupportDescription('');
      setSupportPriority('normal');
      await loadSupportCases();
    } catch {
      setAuthError('The support case could not be created.');
    } finally {
      setSupportBusy(false);
    }
  };

  const updateSupportCase = async (supportCase: SupportCase, status: SupportCase['status']) => {
    if (!user?.access_token || !selectedTenantId || !currentUser?.isPlatformAdmin) return;
    const responseText = supportResponses[supportCase.id]?.trim();
    if (status === 'resolved' && !responseText && !supportCase.latestResponse) {
      setAuthError('Add a customer-visible response before resolving the support case.');
      return;
    }
    setSupportBusy(true);
    setAuthError('');
    try {
      const response = await fetch(
        `${config.apiUrl}/v1/tenants/${selectedTenantId}/support-cases/${supportCase.id}`,
        {
          method: 'PATCH',
          headers: {
            authorization: `Bearer ${user.access_token}`,
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            status,
            ...(responseText ? { response: responseText } : {}),
          }),
        },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setSupportResponses((current) => {
        const next = { ...current };
        delete next[supportCase.id];
        return next;
      });
      await loadSupportCases();
    } catch {
      setAuthError('The support case could not be updated.');
    } finally {
      setSupportBusy(false);
    }
  };

  const createTenant = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (
      !user?.access_token ||
      !newTenantName.trim() ||
      !newTenantSlug.trim() ||
      !newTenantAdministratorEmail.trim()
    ) {
      return;
    }
    setTenantLifecycleBusy(true);
    setAuthError('');
    try {
      const response = await fetch(`${config.apiUrl}/v1/platform/tenants`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${user.access_token}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          name: newTenantName.trim(),
          slug: newTenantSlug.trim().toLowerCase(),
          administratorEmail: newTenantAdministratorEmail.trim().toLowerCase(),
        }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const body = (await response.json()) as { tenant: Tenant };
      setNewTenantName('');
      setNewTenantSlug('');
      setNewTenantAdministratorEmail('');
      setSelectedTenantId(body.tenant.id);
      await loadPlatformTenants();
    } catch {
      setAuthError(
        'The organisation could not be created. Check that its slug and administrator are unique.',
      );
    } finally {
      setTenantLifecycleBusy(false);
    }
  };

  const updateTenantLifecycle = async (tenantId: string, update: Record<string, unknown>) => {
    if (!user?.access_token) return;
    setTenantLifecycleBusy(true);
    setAuthError('');
    try {
      const response = await fetch(`${config.apiUrl}/v1/platform/tenants/${tenantId}/lifecycle`, {
        method: 'PATCH',
        headers: {
          authorization: `Bearer ${user.access_token}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify(update),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const updated = (await response.json()) as Tenant;
      setTenant((current) => (current?.id === updated.id ? updated : current));
      await loadPlatformTenants();
    } catch {
      setAuthError('The organisation lifecycle could not be updated.');
    } finally {
      setTenantLifecycleBusy(false);
    }
  };

  const migrateLegacyRoles = async () => {
    if (!user?.access_token || !currentUser?.isPlatformAdmin) return;
    setRoleMigrationBusy(true);
    setRoleMigrationMessage('');
    try {
      const response = await fetch(
        `${config.apiUrl}/v1/platform/memberships/migrate-legacy-roles`,
        {
          method: 'POST',
          headers: { authorization: `Bearer ${user.access_token}` },
        },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = (await response.json()) as { migrated: number; tenantCount: number };
      setRoleMigrationMessage(
        `${result.migrated} membership${result.migrated === 1 ? '' : 's'} migrated across ${result.tenantCount} organisation${result.tenantCount === 1 ? '' : 's'}.`,
      );
    } catch {
      setRoleMigrationMessage('Legacy role migration could not be completed.');
    } finally {
      setRoleMigrationBusy(false);
    }
  };

  const updateTenantMfaPolicy = async (mfaRequired: boolean) => {
    if (!user?.access_token || !selectedTenantId || !canAdministerMembers) return;
    setTenantMfaBusy(true);
    setAuthError('');
    try {
      const response = await fetch(
        `${config.apiUrl}/v1/tenants/${selectedTenantId}/security/mfa-policy`,
        {
          method: 'PATCH',
          headers: {
            authorization: `Bearer ${user.access_token}`,
            'content-type': 'application/json',
          },
          body: JSON.stringify({ mfaRequired }),
        },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const updated = (await response.json()) as Tenant;
      setTenant(updated);
      setCurrentUser((current) =>
        current
          ? {
              ...current,
              mfaRequiredTenantIds: mfaRequired
                ? [...new Set([...current.mfaRequiredTenantIds, updated.id])]
                : current.mfaRequiredTenantIds.filter((tenantId) => tenantId !== updated.id),
            }
          : current,
      );
      await loadPlatformTenants();
    } catch {
      setAuthError('The organisation MFA requirement could not be updated.');
    } finally {
      setTenantMfaBusy(false);
    }
  };

  const saveOrganisationStructure = async (structure: {
    locations: NonNullable<Tenant['locations']>;
    teams: NonNullable<Tenant['teams']>;
  }) => {
    if (!user?.access_token || !selectedTenantId || !canAdministerMembers) return;
    setOrganisationStructureBusy(true);
    setAuthError('');
    try {
      const response = await fetch(
        `${config.apiUrl}/v1/tenants/${selectedTenantId}/organisation-structure`,
        {
          method: 'PATCH',
          headers: {
            authorization: `Bearer ${user.access_token}`,
            'content-type': 'application/json',
          },
          body: JSON.stringify(structure),
        },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setTenant((await response.json()) as Tenant);
    } catch {
      setAuthError(
        'Locations and teams could not be saved. Reassign personnel before removing one.',
      );
    } finally {
      setOrganisationStructureBusy(false);
    }
  };

  const giftSubscription = async (tenantId: string) => {
    if (!user?.access_token || giftReason.trim().length < 10) return;
    setTenantLifecycleBusy(true);
    setAuthError('');
    try {
      const response = await fetch(
        `${config.apiUrl}/v1/platform/tenants/${tenantId}/subscription/gift`,
        {
          method: 'POST',
          headers: {
            authorization: `Bearer ${user.access_token}`,
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            durationDays: giftDurationDays,
            reason: giftReason.trim(),
          }),
        },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const body = (await response.json()) as { tenant: Tenant };
      setTenant((current) => (current?.id === tenantId ? body.tenant : current));
      await loadPlatformTenants();
    } catch {
      setAuthError('The complimentary organisation subscription could not be granted.');
    } finally {
      setTenantLifecycleBusy(false);
    }
  };

  const saveBillingProfile = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!user?.access_token || !selectedTenantId) return;
    const addressLines = billingAddress
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    setBillingBusy(true);
    setAuthError('');
    try {
      const response = await fetch(
        `${config.apiUrl}/v1/tenants/${selectedTenantId}/billing-profile`,
        {
          method: 'PATCH',
          headers: {
            authorization: `Bearer ${user.access_token}`,
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            contactName: billingContactName.trim(),
            email: billingEmail.trim().toLowerCase(),
            addressLines,
          }),
        },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const updated = (await response.json()) as Tenant;
      setTenant(updated);
      await loadBilling();
    } catch {
      setAuthError('The organisation billing profile could not be saved.');
    } finally {
      setBillingBusy(false);
    }
  };

  const startSubscriptionCheckout = async () => {
    if (!user?.access_token || !selectedTenantId) return;
    setBillingBusy(true);
    setAuthError('');
    try {
      const response = await fetch(
        `${config.apiUrl}/v1/tenants/${selectedTenantId}/subscription/checkout`,
        {
          method: 'POST',
          headers: { authorization: `Bearer ${user.access_token}` },
        },
      );
      const body = (await response.json()) as { url?: string; message?: string };
      if (!response.ok || !body.url) throw new Error(body.message ?? `HTTP ${response.status}`);
      window.location.assign(body.url);
    } catch {
      setAuthError('Stripe checkout could not be started. No payment was taken.');
      setBillingBusy(false);
    }
  };

  const downloadInvoice = async (invoice: ApplicationInvoice) => {
    if (!user?.access_token || !selectedTenantId) return;
    const response = await fetch(
      `${config.apiUrl}/v1/tenants/${selectedTenantId}/invoices/${invoice.id}/document`,
      { headers: { authorization: `Bearer ${user.access_token}` } },
    );
    if (!response.ok) {
      setAuthError('The invoice document could not be downloaded.');
      return;
    }
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = `${invoice.invoiceNumber}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const requestOffboarding = async () => {
    if (!user?.access_token || !selectedTenantId || offboardingReason.trim().length < 10) return;
    setTenantLifecycleBusy(true);
    setAuthError('');
    try {
      const response = await fetch(
        `${config.apiUrl}/v1/tenants/${selectedTenantId}/offboarding-request`,
        {
          method: 'POST',
          headers: {
            authorization: `Bearer ${user.access_token}`,
            'content-type': 'application/json',
          },
          body: JSON.stringify({ reason: offboardingReason.trim() }),
        },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setTenant((await response.json()) as Tenant);
      setOffboardingReason('');
      await loadPlatformTenants();
    } catch {
      setAuthError('The offboarding request could not be recorded.');
    } finally {
      setTenantLifecycleBusy(false);
    }
  };

  const createQualityItem = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!user?.access_token || !selectedTenantId || !itemTitle.trim()) return;
    setSavingItem(true);
    setAuthError('');
    try {
      const workflowPayload =
        activeKind === 'plan'
          ? {
              planDetails: {
                obligation: planObligation.trim(),
                objective: planObjective.trim(),
                successMeasures: planSuccessMeasures
                  .split(/\r?\n/)
                  .map((measure) => measure.trim())
                  .filter(Boolean),
              },
            }
          : activeKind === 'schedule'
            ? {
                scheduleDetails: {
                  activity: scheduleActivity.trim(),
                  cadence: scheduleCadence,
                  timezone: 'Australia/Sydney',
                },
              }
            : activeKind === 'audit'
              ? {
                  auditDetails: {
                    scope: auditScope.trim(),
                    criteria: auditCriteria
                      .split(/\r?\n/)
                      .map((criterion) => criterion.trim())
                      .filter(Boolean),
                    sampling: auditSampling.trim(),
                  },
                }
              : {
                  surveyDetails: {
                    audience: surveyAudience.trim(),
                    responseMode: 'anonymous' as const,
                    consentStatement: surveyConsentStatement.trim(),
                  },
                  surveyQuestions: surveyQuestions
                    .split(/\r?\n/)
                    .map((question) => question.trim())
                    .filter(Boolean),
                };
      const response = await fetch(
        `${config.apiUrl}/v1/tenants/${selectedTenantId}/quality-items`,
        {
          method: 'POST',
          headers: {
            authorization: `Bearer ${user.access_token}`,
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            kind: activeKind,
            title: itemTitle.trim(),
            description: itemDescription.trim(),
            ...(itemDueAt ? { dueAt: new Date(itemDueAt).toISOString() } : {}),
            ...workflowPayload,
          }),
        },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setItemTitle('');
      setItemDescription('');
      setItemDueAt('');
      setPlanObligation('');
      setPlanObjective('');
      setPlanSuccessMeasures('');
      setScheduleActivity('');
      setScheduleCadence('one_off');
      setAuditScope('');
      setAuditCriteria('');
      setAuditSampling('');
      setSurveyAudience('');
      setSurveyQuestions('');
      await loadQualityItems();
    } catch {
      setAuthError('The quality record could not be saved. Check your role and try again.');
    } finally {
      setSavingItem(false);
    }
  };

  const applySurveyTemplate = (templateId: string) => {
    const template = surveyTemplates.find(
      (candidate) => candidate.id === templateId && candidate.status === 'active',
    );
    if (!template) return;
    setItemDescription(template.description);
    setSurveyAudience(template.audience);
    setSurveyConsentStatement(template.consentStatement);
    setSurveyQuestions(template.questions.join('\n'));
  };

  const createSurveyTemplate = async () => {
    if (
      !user?.access_token ||
      !selectedTenantId ||
      !surveyTemplateName.trim() ||
      !surveyAudience.trim() ||
      !surveyConsentStatement.trim()
    ) {
      return;
    }
    const questions = surveyQuestions
      .split(/\r?\n/)
      .map((question) => question.trim())
      .filter(Boolean);
    if (questions.length === 0) return;
    const response = await fetch(
      `${config.apiUrl}/v1/tenants/${selectedTenantId}/survey-templates`,
      {
        method: 'POST',
        headers: {
          authorization: `Bearer ${user.access_token}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          name: surveyTemplateName.trim(),
          description: itemDescription.trim(),
          audience: surveyAudience.trim(),
          consentStatement: surveyConsentStatement.trim(),
          questions,
        }),
      },
    );
    if (!response.ok) {
      setAuthError('The survey template could not be saved. Check its name and questions.');
      return;
    }
    setSurveyTemplateName('');
    await loadSurveyTemplates();
  };

  const archiveSurveyTemplate = async (template: SurveyTemplate) => {
    if (!user?.access_token || !selectedTenantId) return;
    const response = await fetch(
      `${config.apiUrl}/v1/tenants/${selectedTenantId}/survey-templates/${template.id}`,
      {
        method: 'PATCH',
        headers: {
          authorization: `Bearer ${user.access_token}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({ status: 'archived' }),
      },
    );
    if (!response.ok) {
      setAuthError('The survey template could not be archived.');
      return;
    }
    await loadSurveyTemplates();
  };

  const updateQualityItem = async (
    item: QualityItem,
    input: Partial<Pick<QualityItem, 'status' | 'completionSummary' | 'ownerId'>>,
  ) => {
    if (!user?.access_token || !selectedTenantId) return;
    const response = await fetch(
      `${config.apiUrl}/v1/tenants/${selectedTenantId}/quality-items/${item.id}`,
      {
        method: 'PATCH',
        headers: {
          authorization: `Bearer ${user.access_token}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify(input),
      },
    );
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { message?: string };
      setAuthError(body.message ?? 'The record status could not be updated.');
      return;
    }
    setCompletionSummaries((current) => {
      const next = { ...current };
      delete next[item.id];
      return next;
    });
    await loadQualityItems();
  };

  const updateQualityItemStatus = async (item: QualityItem, status: QualityItemStatus) => {
    const completionSummary = completionSummaries[item.id]?.trim();
    await updateQualityItem(item, {
      status,
      ...(status === 'complete' && completionSummary ? { completionSummary } : {}),
    });
  };

  const updatePlanApproval = async (
    item: QualityItem,
    action: 'submit' | 'approve' | 'request_changes',
  ) => {
    if (!user?.access_token || !selectedTenantId) return;
    const comment = planApprovalComments[item.id]?.trim();
    const response = await fetch(
      `${config.apiUrl}/v1/tenants/${selectedTenantId}/quality-items/${item.id}/approval`,
      {
        method: 'POST',
        headers: {
          authorization: `Bearer ${user.access_token}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({ action, ...(comment ? { comment } : {}) }),
      },
    );
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { message?: string };
      setAuthError(body.message ?? 'The plan approval could not be updated.');
      return;
    }
    setPlanApprovalComments((current) => {
      const next = { ...current };
      delete next[item.id];
      return next;
    });
    await loadQualityItems();
  };

  const reopenPlan = async (item: QualityItem) => {
    if (!user?.access_token || !selectedTenantId) return;
    const reason = planReopenReasons[item.id]?.trim();
    if (!reason) {
      setAuthError('Add a reason before reopening the plan.');
      return;
    }
    const response = await fetch(
      `${config.apiUrl}/v1/tenants/${selectedTenantId}/quality-items/${item.id}/reopen`,
      {
        method: 'POST',
        headers: {
          authorization: `Bearer ${user.access_token}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({ reason }),
      },
    );
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { message?: string };
      setAuthError(body.message ?? 'The completed plan could not be reopened.');
      return;
    }
    setPlanReopenReasons((current) => {
      const next = { ...current };
      delete next[item.id];
      return next;
    });
    await loadQualityItems();
  };

  const loadPlanActions = async (planId: string) => {
    if (!user?.access_token || !selectedTenantId) return;
    const response = await fetch(
      `${config.apiUrl}/v1/tenants/${selectedTenantId}/quality-items/${planId}/actions`,
      { headers: { authorization: `Bearer ${user.access_token}` } },
    );
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const body = (await response.json()) as { items: PlanAction[] };
    setPlanActions((current) => ({ ...current, [planId]: body.items }));
  };

  const togglePlanActions = async (planId: string) => {
    if (expandedPlanId === planId) {
      setExpandedPlanId('');
      return;
    }
    setExpandedPlanId(planId);
    setAuthError('');
    try {
      await loadPlanActions(planId);
    } catch {
      setAuthError('Planning actions could not be loaded.');
    }
  };

  const createPlanAction = async (event: FormEvent<HTMLFormElement>, planId: string) => {
    event.preventDefault();
    if (!user?.access_token || !selectedTenantId || !planActionDescription.trim()) return;
    const response = await fetch(
      `${config.apiUrl}/v1/tenants/${selectedTenantId}/quality-items/${planId}/actions`,
      {
        method: 'POST',
        headers: {
          authorization: `Bearer ${user.access_token}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          description: planActionDescription.trim(),
          ...(planActionDueAt ? { dueAt: new Date(planActionDueAt).toISOString() } : {}),
          evidenceFileIds: planActionEvidenceFileId ? [planActionEvidenceFileId] : [],
        }),
      },
    );
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { message?: string };
      setAuthError(body.message ?? 'The planning action could not be created.');
      return;
    }
    setPlanActionDescription('');
    setPlanActionDueAt('');
    setPlanActionEvidenceFileId('');
    await loadPlanActions(planId);
  };

  const updatePlanAction = async (
    action: PlanAction,
    input: Partial<
      Pick<
        PlanAction,
        'status' | 'evidenceFileIds' | 'completionSummary' | 'description' | 'dueAt' | 'ownerId'
      >
    >,
  ) => {
    if (!user?.access_token || !selectedTenantId) return;
    const response = await fetch(
      `${config.apiUrl}/v1/tenants/${selectedTenantId}/quality-items/${action.planId}/actions/${action.id}`,
      {
        method: 'PATCH',
        headers: {
          authorization: `Bearer ${user.access_token}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify(input),
      },
    );
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { message?: string };
      setAuthError(body.message ?? 'The planning action could not be updated.');
      return;
    }
    setPlanActionCompletionSummaries((current) => {
      const next = { ...current };
      delete next[action.id];
      return next;
    });
    await loadPlanActions(action.planId);
  };

  const updatePlanActionStatus = async (action: PlanAction, status: PlanActionStatus) => {
    const completionSummary = planActionCompletionSummaries[action.id]?.trim();
    await updatePlanAction(action, {
      status,
      ...(status === 'complete' && completionSummary ? { completionSummary } : {}),
    });
  };

  const linkPlanActionEvidence = async (action: PlanAction, fileId: string) => {
    if (!fileId || action.evidenceFileIds.includes(fileId)) return;
    await updatePlanAction(action, {
      evidenceFileIds: [...action.evidenceFileIds, fileId],
    });
  };

  const loadAuditFindings = async (auditId: string) => {
    if (!user?.access_token || !selectedTenantId) return;
    const response = await fetch(
      `${config.apiUrl}/v1/tenants/${selectedTenantId}/quality-items/${auditId}/findings`,
      { headers: { authorization: `Bearer ${user.access_token}` } },
    );
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const body = (await response.json()) as { items: AuditFinding[] };
    setAuditFindings((current) => ({ ...current, [auditId]: body.items }));
  };

  const toggleAuditFindings = async (auditId: string) => {
    if (expandedAuditId === auditId) {
      setExpandedAuditId('');
      return;
    }
    setExpandedAuditId(auditId);
    setAuthError('');
    try {
      await loadAuditFindings(auditId);
    } catch {
      setAuthError('Internal-audit findings could not be loaded.');
    }
  };

  const createAuditFinding = async (event: FormEvent<HTMLFormElement>, auditId: string) => {
    event.preventDefault();
    if (
      !user?.access_token ||
      !selectedTenantId ||
      !auditFindingTitle.trim() ||
      !auditFindingStatement.trim()
    ) {
      return;
    }
    const response = await fetch(
      `${config.apiUrl}/v1/tenants/${selectedTenantId}/quality-items/${auditId}/findings`,
      {
        method: 'POST',
        headers: {
          authorization: `Bearer ${user.access_token}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          title: auditFindingTitle.trim(),
          statement: auditFindingStatement.trim(),
          classification: auditFindingClassification,
          ...(auditFindingCorrectiveAction.trim()
            ? { correctiveAction: auditFindingCorrectiveAction.trim() }
            : {}),
          ...(auditFindingDueAt ? { dueAt: new Date(auditFindingDueAt).toISOString() } : {}),
          evidenceFileIds: auditFindingEvidenceFileId ? [auditFindingEvidenceFileId] : [],
        }),
      },
    );
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { message?: string };
      setAuthError(body.message ?? 'The internal-audit finding could not be created.');
      return;
    }
    setAuditFindingTitle('');
    setAuditFindingStatement('');
    setAuditFindingClassification('observation');
    setAuditFindingCorrectiveAction('');
    setAuditFindingDueAt('');
    setAuditFindingEvidenceFileId('');
    await loadAuditFindings(auditId);
  };

  const updateAuditFinding = async (
    finding: AuditFinding,
    input: Partial<
      Pick<
        AuditFinding,
        | 'status'
        | 'evidenceFileIds'
        | 'closureEvidenceFileIds'
        | 'closureSummary'
        | 'correctiveAction'
        | 'dueAt'
      >
    >,
  ) => {
    if (!user?.access_token || !selectedTenantId) return;
    const response = await fetch(
      `${config.apiUrl}/v1/tenants/${selectedTenantId}/quality-items/${finding.auditId}/findings/${finding.id}`,
      {
        method: 'PATCH',
        headers: {
          authorization: `Bearer ${user.access_token}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify(input),
      },
    );
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { message?: string };
      setAuthError(body.message ?? 'The internal-audit finding could not be updated.');
      return;
    }
    setAuditFindingClosureSummaries((current) => {
      const next = { ...current };
      delete next[finding.id];
      return next;
    });
    setAuditFindingClosureEvidence((current) => {
      const next = { ...current };
      delete next[finding.id];
      return next;
    });
    await loadAuditFindings(finding.auditId);
  };

  const updateAuditFindingStatus = async (finding: AuditFinding, status: AuditFindingStatus) => {
    const closureSummary = auditFindingClosureSummaries[finding.id]?.trim();
    const closureFileId = auditFindingClosureEvidence[finding.id];
    await updateAuditFinding(finding, {
      status,
      ...(status === 'closed' && closureSummary ? { closureSummary } : {}),
      ...(status === 'closed' && closureFileId
        ? {
            closureEvidenceFileIds: [
              ...new Set([...finding.closureEvidenceFileIds, closureFileId]),
            ],
          }
        : {}),
    });
  };

  const linkAuditFindingEvidence = async (finding: AuditFinding, fileId: string) => {
    if (!fileId || finding.evidenceFileIds.includes(fileId)) return;
    await updateAuditFinding(finding, {
      evidenceFileIds: [...finding.evidenceFileIds, fileId],
    });
  };

  const loadScheduleOccurrences = async (scheduleId: string) => {
    if (!user?.access_token || !selectedTenantId) return;
    const response = await fetch(
      `${config.apiUrl}/v1/tenants/${selectedTenantId}/quality-items/${scheduleId}/occurrences`,
      { headers: { authorization: `Bearer ${user.access_token}` } },
    );
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const body = (await response.json()) as { items: ScheduleOccurrence[] };
    setScheduleOccurrences((current) => ({ ...current, [scheduleId]: body.items }));
  };

  const toggleScheduleOccurrences = async (scheduleId: string) => {
    if (expandedScheduleId === scheduleId) {
      setExpandedScheduleId('');
      return;
    }
    setExpandedScheduleId(scheduleId);
    setAuthError('');
    try {
      await loadScheduleOccurrences(scheduleId);
    } catch {
      setAuthError('Schedule occurrences could not be loaded.');
    }
  };

  const updateScheduleOccurrence = async (
    occurrence: ScheduleOccurrence,
    input: Partial<
      Pick<
        ScheduleOccurrence,
        | 'status'
        | 'scheduledFor'
        | 'ownerId'
        | 'evidenceFileIds'
        | 'resolutionSummary'
        | 'skipReason'
        | 'skipReasonDetails'
      >
    > & { rescheduleReason?: string },
  ) => {
    if (!user?.access_token || !selectedTenantId) return false;
    const response = await fetch(
      `${config.apiUrl}/v1/tenants/${selectedTenantId}/quality-items/${occurrence.scheduleId}/occurrences/${occurrence.id}`,
      {
        method: 'PATCH',
        headers: {
          authorization: `Bearer ${user.access_token}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify(input),
      },
    );
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { message?: string };
      setAuthError(body.message ?? 'The schedule occurrence could not be updated.');
      return false;
    }
    if (input.status === 'complete' || input.status === 'skipped') {
      setScheduleOccurrenceSummaries((current) => {
        const next = { ...current };
        delete next[occurrence.id];
        return next;
      });
      setScheduleOccurrenceSkipReasons((current) => {
        const next = { ...current };
        delete next[occurrence.id];
        return next;
      });
      setScheduleOccurrenceSkipDetails((current) => {
        const next = { ...current };
        delete next[occurrence.id];
        return next;
      });
    }
    await loadScheduleOccurrences(occurrence.scheduleId);
    return true;
  };

  const updateScheduleOccurrenceStatus = async (
    occurrence: ScheduleOccurrence,
    status: ScheduleOccurrenceStatus,
  ) => {
    const resolutionSummary = scheduleOccurrenceSummaries[occurrence.id]?.trim();
    await updateScheduleOccurrence(occurrence, {
      status,
      ...((status === 'complete' || status === 'skipped') && resolutionSummary
        ? { resolutionSummary }
        : {}),
    });
  };

  const rescheduleOccurrence = async (occurrence: ScheduleOccurrence) => {
    const input = scheduleOccurrenceReschedules[occurrence.id];
    if (!input?.scheduledFor || !input.reason.trim()) {
      setAuthError('Choose a future date and add a reason before rescheduling.');
      return;
    }
    const updated = await updateScheduleOccurrence(occurrence, {
      scheduledFor: new Date(input.scheduledFor).toISOString(),
      rescheduleReason: input.reason.trim(),
    });
    if (!updated) return;
    setScheduleOccurrenceReschedules((current) => {
      const next = { ...current };
      delete next[occurrence.id];
      return next;
    });
  };

  const skipOccurrence = async (occurrence: ScheduleOccurrence) => {
    const skipReason = scheduleOccurrenceSkipReasons[occurrence.id];
    const skipReasonDetails = scheduleOccurrenceSkipDetails[occurrence.id]?.trim();
    const resolutionSummary = scheduleOccurrenceSummaries[occurrence.id]?.trim();
    if (!skipReason || !resolutionSummary || (skipReason === 'other' && !skipReasonDetails)) {
      setAuthError(
        'Select a skip reason, add other details when required, and retain a resolution summary.',
      );
      return;
    }
    await updateScheduleOccurrence(occurrence, {
      status: 'skipped',
      skipReason,
      ...(skipReasonDetails ? { skipReasonDetails } : {}),
      resolutionSummary,
    });
  };

  const linkScheduleOccurrenceEvidence = async (occurrence: ScheduleOccurrence, fileId: string) => {
    if (!fileId || occurrence.evidenceFileIds.includes(fileId)) return;
    await updateScheduleOccurrence(occurrence, {
      evidenceFileIds: [...occurrence.evidenceFileIds, fileId],
    });
  };

  const shareSurvey = async (item: QualityItem) => {
    if (!user?.access_token || !selectedTenantId) return;
    const response = await fetch(
      `${config.apiUrl}/v1/tenants/${selectedTenantId}/quality-items/${item.id}/survey-token`,
      {
        method: 'POST',
        headers: {
          authorization: `Bearer ${user.access_token}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({ expiresInDays: 30, maximumResponses: 100 }),
      },
    );
    if (!response.ok) {
      setAuthError('The survey link could not be created. Add questions and check your role.');
      return;
    }
    const body = (await response.json()) as { path: string; token: string };
    setSurveyShareLink(`${window.location.origin}${body.path}`);
    setSurveyShareToken(body.token);
    await loadSurveyLinks(item);
  };

  const loadSurveyLinks = async (item: QualityItem) => {
    if (!user?.access_token || !selectedTenantId) return;
    const response = await fetch(
      `${config.apiUrl}/v1/tenants/${selectedTenantId}/quality-items/${item.id}/survey-tokens`,
      { headers: { authorization: `Bearer ${user.access_token}` } },
    );
    if (!response.ok) {
      setAuthError('Survey links could not be loaded.');
      return;
    }
    const body = (await response.json()) as { items: SurveyGuestToken[] };
    setSurveyLinks((current) => ({ ...current, [item.id]: body.items }));
  };

  const revokeSurveyLink = async (item: QualityItem, token: SurveyGuestToken) => {
    if (!user?.access_token || !selectedTenantId) return;
    const response = await fetch(
      `${config.apiUrl}/v1/tenants/${selectedTenantId}/quality-items/${item.id}/survey-tokens/${token.tokenHash}`,
      {
        method: 'DELETE',
        headers: { authorization: `Bearer ${user.access_token}` },
      },
    );
    if (!response.ok) {
      setAuthError('The survey link could not be revoked.');
      return;
    }
    await loadSurveyLinks(item);
  };

  const loadSurveyResults = async (item: QualityItem) => {
    if (!user?.access_token || !selectedTenantId) return;
    const response = await fetch(
      `${config.apiUrl}/v1/tenants/${selectedTenantId}/quality-items/${item.id}/survey-responses`,
      { headers: { authorization: `Bearer ${user.access_token}` } },
    );
    if (!response.ok) {
      setAuthError('Survey responses could not be loaded.');
      return;
    }
    const body = (await response.json()) as {
      questions: string[];
      items: SurveyResponseRecord[];
    };
    setSurveyResults({ title: item.title, ...body });
  };

  const exportSurveyResponses = async (item: QualityItem) => {
    if (!user?.access_token || !selectedTenantId) return;
    const response = await fetch(
      `${config.apiUrl}/v1/tenants/${selectedTenantId}/quality-items/${item.id}/survey-response-export`,
      { headers: { authorization: `Bearer ${user.access_token}` } },
    );
    if (!response.ok) {
      setAuthError('Survey responses could not be exported.');
      return;
    }
    const body = (await response.json()) as {
      title: string;
      questions: string[];
      generatedAt: string;
      items: SurveyResponseRecord[];
    };
    const rows = [
      ['Response ID', 'Submitted at', 'Consent recorded at', ...body.questions],
      ...body.items.map((result) => [
        result.id,
        result.submittedAt,
        result.consentedAt ?? '',
        ...body.questions.map((_, index) => result.answers[index] ?? ''),
      ]),
    ];
    const csv = rows.map((row) => row.map(csvCell).join(',')).join('\r\n');
    downloadTextFile(
      `\uFEFF${csv}`,
      `quality-vet-survey-${safeFileName(body.title) || item.id}-${body.generatedAt.slice(0, 10)}.csv`,
      'text/csv;charset=utf-8',
    );
  };

  const sendSurveyInvitation = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!user?.access_token || !selectedTenantId || !surveyShareToken || !notificationEmail) return;
    const response = await fetch(`${config.apiUrl}/v1/tenants/${selectedTenantId}/notifications`, {
      method: 'POST',
      headers: {
        authorization: `Bearer ${user.access_token}`,
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        template: 'survey-invitation',
        recipient: notificationEmail,
        data: { surveyToken: surveyShareToken },
      }),
    });
    if (!response.ok) {
      setAuthError('The survey invitation could not be queued.');
      return;
    }
    setNotificationEmail('');
  };

  const uploadFile = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!user?.access_token || !selectedTenantId || !selectedFile) return;
    if (selectedFile.size <= 0 || selectedFile.size > 25 * 1024 * 1024) {
      setAuthError('Select a non-empty file no larger than 25 MB.');
      return;
    }
    setUploadingFile(true);
    setAuthError('');
    try {
      const prepare = await fetch(
        `${config.apiUrl}/v1/tenants/${selectedTenantId}/files/upload-url`,
        {
          method: 'POST',
          headers: {
            authorization: `Bearer ${user.access_token}`,
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            fileName: selectedFile.name,
            contentType: selectedFile.type || 'application/octet-stream',
          }),
        },
      );
      if (!prepare.ok) throw new Error(`HTTP ${prepare.status}`);
      const prepared = (await prepare.json()) as {
        uploadUrl: string;
        file: FileRecord;
      };
      const upload = await fetch(prepared.uploadUrl, {
        method: 'PUT',
        headers: {
          'content-type': prepared.file.contentType,
          'content-disposition': 'attachment',
        },
        body: selectedFile,
      });
      if (!upload.ok) throw new Error(`S3 HTTP ${upload.status}`);
      const complete = await fetch(
        `${config.apiUrl}/v1/tenants/${selectedTenantId}/files/${prepared.file.id}/complete`,
        {
          method: 'POST',
          headers: { authorization: `Bearer ${user.access_token}` },
        },
      );
      if (!complete.ok) throw new Error(`Completion HTTP ${complete.status}`);
      setSelectedFile(null);
      await loadFiles();
    } catch {
      setAuthError('The file upload could not be completed.');
    } finally {
      setUploadingFile(false);
    }
  };

  const downloadFile = async (file: FileRecord) => {
    if (!user?.access_token || !selectedTenantId) return;
    const response = await fetch(
      `${config.apiUrl}/v1/tenants/${selectedTenantId}/files/${file.id}/download-url`,
      { headers: { authorization: `Bearer ${user.access_token}` } },
    );
    if (!response.ok) {
      setAuthError('The file is not available for download.');
      return;
    }
    const body = (await response.json()) as { downloadUrl: string };
    window.location.assign(body.downloadUrl);
  };

  const deleteFile = async (file: FileRecord) => {
    if (!user?.access_token || !selectedTenantId) return;
    const response = await fetch(
      `${config.apiUrl}/v1/tenants/${selectedTenantId}/files/${file.id}`,
      {
        method: 'DELETE',
        headers: { authorization: `Bearer ${user.access_token}` },
      },
    );
    if (!response.ok) {
      setAuthError('The file could not be deleted.');
      return;
    }
    await loadFiles();
  };

  const inviteMember = async (
    event: FormEvent<HTMLFormElement>,
    assignments: Pick<Membership, 'locationIds' | 'teamIds' | 'teamLeaderTeamIds'>,
  ) => {
    event.preventDefault();
    if (!user?.access_token || !selectedTenantId || !inviteEmail) return;
    const response = await fetch(`${config.apiUrl}/v1/tenants/${selectedTenantId}/memberships`, {
      method: 'POST',
      headers: {
        authorization: `Bearer ${user.access_token}`,
        'content-type': 'application/json',
      },
      body: JSON.stringify({ email: inviteEmail, roles: [inviteRole], ...assignments }),
    });
    if (!response.ok) {
      setAuthError('The member invitation could not be sent.');
      return;
    }
    setInviteEmail('');
    await loadMembers();
  };

  const updateMembership = async (
    membership: Membership,
    update: Partial<
      Pick<Membership, 'roles' | 'status' | 'locationIds' | 'teamIds' | 'teamLeaderTeamIds'>
    >,
  ) => {
    if (!user?.access_token || !selectedTenantId) return;
    const response = await fetch(
      `${config.apiUrl}/v1/tenants/${selectedTenantId}/memberships/${membership.userId}`,
      {
        method: 'PATCH',
        headers: {
          authorization: `Bearer ${user.access_token}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify(update),
      },
    );
    if (!response.ok) {
      setAuthError('The membership could not be updated. Keep at least one active administrator.');
      return;
    }
    const updated = (await response.json()) as Membership;
    setCurrentUser((current) =>
      current
        ? {
            ...current,
            memberships: current.memberships.map((item) =>
              item.tenantId === updated.tenantId && item.userId === updated.userId ? updated : item,
            ),
          }
        : current,
    );
    await loadMembers();
  };

  const resetMemberMfa = async (membership: Membership) => {
    if (!user?.access_token || !selectedTenantId || !canAdministerMembers) return;
    setAuthError('');
    const response = await fetch(
      `${config.apiUrl}/v1/tenants/${selectedTenantId}/memberships/${encodeURIComponent(membership.userId)}/mfa-reset`,
      {
        method: 'POST',
        headers: { authorization: `Bearer ${user.access_token}` },
      },
    );
    if (!response.ok) {
      setAuthError(
        'MFA could not be reset. Confirm the person still has active organisation access.',
      );
      return;
    }
    setAuthError(
      `MFA was reset for ${membership.email}. They must enrol again before accessing an organisation that requires MFA.`,
    );
  };

  const activeModule = modules.find((module) => module.kind === activeKind)!;
  const visibleItems = qualityItems.filter((item) => item.kind === activeKind);
  const loginWithCognito = async (session: CognitoUserSession) => {
    if (!manager) throw new Error('Cognito is not configured.');
    const signedInUser = userFromCognitoSession(session);
    await manager.storeUser(signedInUser);
    setAuthError('');
    setUser(signedInUser);
    window.history.replaceState({}, '', '/');
  };

  if (!user && authInitialising) {
    return (
      <main className="login-page" aria-busy="true">
        <p aria-atomic="true" role="status" aria-live="polite">
          Loading sign in…
        </p>
      </main>
    );
  }

  if (!user) {
    return (
      <LoginPanel
        apiUrl={config.apiUrl}
        configured={authConfigured}
        pool={pool}
        publicUrl={config.publicUrl}
        signupEnabled={config.signupEnabled === true}
        error={authError}
        onLogin={loginWithCognito}
      />
    );
  }

  if (workspaceContextInitialising) {
    return (
      <main className="login-page" aria-busy="true">
        <p aria-atomic="true" role="status" aria-live="polite">
          Loading workspaceâ€¦
        </p>
      </main>
    );
  }

  if (currentUser && mfaRequiredForSelectedTenant && !currentUser.mfaEnabled) {
    return (
      <div className="app-layout authenticated-onboarding-layout" data-theme={theme}>
        <a className="skip-link" href="#main-content">
          Skip to main content
        </a>
        <main className="workspace-page mandatory-mfa-enrollment" id="main-content" tabIndex={-1}>
          <AccountSecurityPanel
            accessToken={user.access_token}
            authority={config.cognitoAuthority}
            {...(currentUser.email || user.profile.email
              ? { email: currentUser.email ?? user.profile.email }
              : {})}
            mfaEnabled={false}
            mfaRequired
            pool={pool}
            emailMfaAvailable={emailMfaAvailable}
            visible
            onEnabled={() =>
              setCurrentUser((current) => (current ? { ...current, mfaEnabled: true } : current))
            }
          />
        </main>
      </div>
    );
  }

  if (currentUser?.onboardingRequired && !currentUser.isPlatformAdmin) {
    return (
      <div className="app-layout authenticated-onboarding-layout" data-theme={theme}>
        <a className="skip-link" href="#main-content">
          Skip to main content
        </a>
        <AppUtilityBar
          accessToken={user.access_token}
          apiUrl={config.apiUrl}
          currentUserEmail={currentUser.email ?? user.profile.email ?? 'Authenticated user'}
          currentUserId={currentUser.userId}
          isPlatformAdmin={false}
          members={members}
          tenantId={selectedTenantId}
          tenantRoles={selectedMembership?.roles ?? []}
          timeZone={profile.timezone}
          {...(tenant?.name ? { tenantName: tenant.name } : {})}
        />
        <ProviderOnboarding
          id="main-content"
          apiUrl={config.apiUrl}
          accessToken={user.access_token}
          currentUser={currentUser}
          onCompleted={() => window.location.reload()}
        />
      </div>
    );
  }

  const openWorkspacePage = (page: WorkspacePage) => {
    if (isAdminWorkspacePage(page) && !currentUser?.isPlatformAdmin) return;
    setWorkspacePage(page);
    window.history.replaceState(null, '', page === 'home' ? '/' : `#${page}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const profileEmail = currentUser?.email ?? user.profile.email ?? 'Authenticated user';
  return (
    <div className="app-layout" data-theme={theme}>
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      <aside aria-label="Workspace navigation">
        <a className="brand" href="/">
          quality<span>.vet</span>
        </a>
        <div className="sidebar-footer">
          <AdminMenu
            visible={Boolean(currentUser?.isPlatformAdmin)}
            onOpenArea={openWorkspacePage}
          />
          <UserMenu
            email={profileEmail}
            fullName={
              [profile.firstName, profile.familyName].filter(Boolean).join(' ') || 'Account'
            }
            {...(selectedOrganisationName ? { organisationName: selectedOrganisationName } : {})}
            pictureUrl={profile.pictureUrl}
            theme={theme}
            onOpenProfile={() => openWorkspacePage('profile')}
            onOpenPurchases={() => openWorkspacePage('purchases')}
            onThemeChange={setTheme}
            onLogout={async () => {
              pool?.getCurrentUser()?.signOut();
              await manager?.removeUser();
              window.location.assign(config.logoutUri);
            }}
          />
        </div>
      </aside>
      <AppUtilityBar
        accessToken={user.access_token}
        apiUrl={config.apiUrl}
        currentUserEmail={profileEmail}
        currentUserId={currentUser?.userId ?? user.profile.sub}
        isPlatformAdmin={Boolean(currentUser?.isPlatformAdmin)}
        members={members}
        refreshKey={utilityBarRefreshKey}
        tenantId={selectedTenantId}
        tenantRoles={selectedMembership?.roles ?? []}
        timeZone={profile.timezone}
        onNavigateToMessages={(conversationId?: string) => {
          setInitialConversationId(conversationId);
          openWorkspacePage('messages');
        }}
        onNavigateToNotifications={(notificationId?: string) => {
          setInitialNotificationId(notificationId);
          openWorkspacePage('notifications');
        }}
        {...(selectedOrganisationName ? { tenantName: selectedOrganisationName } : {})}
      />
      <main
        className={workspacePage === 'home' ? 'workspace-empty' : 'workspace-page'}
        id="main-content"
        tabIndex={-1}
      >
        {authError ? (
          <p className="auth-error" role="alert">
            {authError}
          </p>
        ) : null}
        {workspacePage === 'profile' ? (
          <ProfileSettings
            accessToken={user.access_token}
            authority={config.cognitoAuthority}
            email={profileEmail}
            mfaSettings={
              !mfaRequiredForSelectedTenant ? (
                <AccountSecurityPanel
                  accessToken={user.access_token}
                  authority={config.cognitoAuthority}
                  embedded
                  visible={Boolean(user)}
                  pool={pool}
                  email={profileEmail}
                  mfaEnabled={Boolean(currentUser?.mfaEnabled)}
                  emailMfaAvailable={emailMfaAvailable}
                  mfaRequired={false}
                />
              ) : null
            }
            pool={pool}
            profile={profile}
            organisationName={selectedOrganisationName}
            membershipRoles={selectedMembership?.roles}
            membershipStatus={selectedMembership?.status}
            onOpenOrganisation={() => openWorkspacePage('organisation')}
            onReauthenticate={() =>
              manager?.signinRedirect({ prompt: 'login' }) ?? Promise.resolve()
            }
            onProfileChange={setProfile}
          />
        ) : null}
        {workspacePage === 'organisation' ? (
          <OrganisationPage
            busy={organisationStructureBusy}
            editable={canAdministerMembers}
            onSaveStructure={saveOrganisationStructure}
            tenant={tenant}
          />
        ) : null}
        {workspacePage === 'purchases' ? (
          <>
            <PurchasesPanel
              subscriptions={billingData?.subscriptions ?? []}
              {...(tenant?.subscriptionAccessUntil
                ? { accessUntil: tenant.subscriptionAccessUntil }
                : {})}
            />
            <BillingPanel
              visible={Boolean(tenant && canAdministerMembers)}
              tenant={tenant}
              billingData={billingData}
              billingContactName={billingContactName}
              billingEmail={billingEmail}
              billingAddress={billingAddress}
              billingBusy={billingBusy}
              onBillingContactNameChange={setBillingContactName}
              onBillingEmailChange={setBillingEmail}
              onBillingAddressChange={setBillingAddress}
              onSaveProfile={saveBillingProfile}
              onStartCheckout={startSubscriptionCheckout}
              onDownloadInvoice={downloadInvoice}
            />
          </>
        ) : null}
        {workspacePage === 'messages' ? (
          <MessagesPage
            accessToken={user.access_token}
            apiUrl={config.apiUrl}
            currentUserEmail={profileEmail}
            currentUserId={currentUser?.userId ?? user.profile.sub}
            members={members}
            tenantId={selectedTenantId}
            tenantRoles={selectedMembership?.roles ?? []}
            {...(initialConversationId ? { initialConversationId } : {})}
            onInitialConversationConsumed={() => setInitialConversationId(undefined)}
            onViewed={() => setUtilityBarRefreshKey((key) => key + 1)}
          />
        ) : null}
        {workspacePage === 'notifications' ? (
          <NotificationsPage
            accessToken={user.access_token}
            apiUrl={config.apiUrl}
            currentUserId={currentUser?.userId ?? user.profile.sub}
            isPlatformAdmin={Boolean(currentUser?.isPlatformAdmin)}
            tenantId={selectedTenantId}
            tenantRoles={selectedMembership?.roles ?? []}
            timeZone={profile.timezone}
            {...(initialNotificationId ? { initialNotificationId } : {})}
            onInitialNotificationConsumed={() => setInitialNotificationId(undefined)}
            onNotificationRead={() => setUtilityBarRefreshKey((key) => key + 1)}
            {...(selectedOrganisationName ? { tenantName: selectedOrganisationName } : {})}
          />
        ) : null}
        {workspacePage === 'admin-announcements' && currentUser?.isPlatformAdmin ? (
          <Suspense fallback={<p>Loading announcements…</p>}>
            <AnnouncementsAdminPanel
              accessToken={user.access_token}
              apiUrl={config.apiUrl}
              tenants={platformTenants}
              visible
            />
          </Suspense>
        ) : null}
        {workspacePage === 'admin-organisations' && currentUser?.isPlatformAdmin ? (
          <OrganisationDirectory
            accessToken={user.access_token}
            apiUrl={config.apiUrl}
            tenants={platformTenants}
            busy={tenantLifecycleBusy}
            giftDurationDays={giftDurationDays}
            giftReason={giftReason}
            onGiftDurationDaysChange={setGiftDurationDays}
            onGiftReasonChange={setGiftReason}
            onUpdateTenantLifecycle={updateTenantLifecycle}
            onGiftSubscription={giftSubscription}
          />
        ) : null}
        {workspacePage === 'admin-people' && currentUser?.isPlatformAdmin ? (
          <PlatformPeopleDirectory
            accessToken={user.access_token}
            apiUrl={config.apiUrl}
            tenants={platformTenants}
          />
        ) : null}
        {workspacePage === 'admin-tasks' && currentUser?.isPlatformAdmin ? (
          <Suspense fallback={<p>Loading tasks…</p>}>
            <TasksTimeLoggingPanel
              accessToken={user.access_token}
              apiUrl={config.apiUrl}
              timeZone={profile.timezone}
            />
          </Suspense>
        ) : null}
        {workspacePage === 'platform-admin-title' && currentUser?.isPlatformAdmin ? (
          <PlatformAdminPanel
            accessToken={user.access_token}
            apiUrl={config.apiUrl}
            visible={Boolean(currentUser?.isPlatformAdmin)}
            tenants={platformTenants}
            newTenantName={newTenantName}
            newTenantSlug={newTenantSlug}
            newTenantAdministratorEmail={newTenantAdministratorEmail}
            busy={tenantLifecycleBusy}
            giftDurationDays={giftDurationDays}
            giftReason={giftReason}
            roleMigrationBusy={roleMigrationBusy}
            roleMigrationMessage={roleMigrationMessage}
            onNewTenantNameChange={setNewTenantName}
            onNewTenantSlugChange={setNewTenantSlug}
            onNewTenantAdministratorEmailChange={setNewTenantAdministratorEmail}
            onGiftDurationDaysChange={setGiftDurationDays}
            onGiftReasonChange={setGiftReason}
            onCreateTenant={createTenant}
            onUpdateTenantLifecycle={updateTenantLifecycle}
            onGiftSubscription={giftSubscription}
            onMigrateLegacyRoles={migrateLegacyRoles}
          />
        ) : null}
        {!legacyLandingPageRemoved ? (
          <>
            <header>
              <div>
                <p>Workspace</p>
                <h1>Quality overview</h1>
              </div>
              <a className="profile-link" href="#profile">
                {user.profile.email ?? 'Profile'}
              </a>
            </header>
            {authError ? <p className="auth-error">{authError}</p> : null}
            <ProfileSettings
              accessToken={user.access_token}
              authority={config.cognitoAuthority}
              email={currentUser?.email ?? user.profile.email ?? 'Authenticated user'}
              pool={pool}
              profile={profile}
              organisationName={selectedOrganisationName}
              membershipRoles={selectedMembership?.roles}
              membershipStatus={selectedMembership?.status}
              onProfileChange={setProfile}
              onReauthenticate={() =>
                manager?.signinRedirect({
                  prompt: 'login',
                  extraQueryParams: { login_hint: String(profileEmail) },
                }) ?? Promise.resolve()
              }
            />
            <AccountSecurityPanel
              visible={Boolean(user)}
              pool={pool}
              {...(currentUser?.email || user?.profile.email
                ? { email: currentUser?.email ?? user?.profile.email }
                : {})}
              mfaEnabled={Boolean(currentUser?.mfaEnabled)}
              emailMfaAvailable={emailMfaAvailable}
              mfaRequired={mfaRequiredForSelectedTenant}
            />
            <TenantMfaPolicyPanel
              visible={Boolean(tenant && canAdministerMembers)}
              required={Boolean(tenant?.mfaRequired)}
              busy={tenantMfaBusy}
              onChange={updateTenantMfaPolicy}
            />
            {tenant?.tgaProviderCode && tenant.providerVerificationStatus !== 'verified' ? (
              <div className="provider-verification-banner">
                <strong>
                  {tenant.providerVerificationStatus === 'manual_review_required'
                    ? 'Provider verification needs manual review'
                    : 'Provider verification is pending'}
                </strong>
                <span>
                  Core quality work is available. Member administration, paid activation, guest
                  survey links, outgoing notifications and exports remain locked until verification.
                </span>
              </div>
            ) : null}
            <section className="welcome" id="overview">
              <div>
                <p className="eyebrow">Continuous improvement</p>
                <h2>
                  Your quality system,
                  <br />
                  in one clear view.
                </h2>
              </div>
              <div className="cycle">
                <span>Plan</span>
                <span>Monitor</span>
                <span>Evaluate</span>
                <strong>Improve</strong>
              </div>
              {qualitySummary ? (
                <div className="quality-summary" aria-label="Quality summary" role="group">
                  <div>
                    <strong>{qualitySummary.total}</strong>
                    <span>Total records</span>
                  </div>
                  <div>
                    <strong>{qualitySummary.byStatus.in_progress}</strong>
                    <span>In progress</span>
                  </div>
                  <div>
                    <strong>{qualitySummary.overdue}</strong>
                    <span>Overdue</span>
                  </div>
                  <div>
                    <strong>{qualitySummary.dueSoon}</strong>
                    <span>Due in {qualitySummary.horizonDays} days</span>
                  </div>
                  <div>
                    <strong>{qualitySummary.completionRatePercent}%</strong>
                    <span>Completion rate</span>
                  </div>
                </div>
              ) : null}
            </section>
            {qualitySummary ? (
              <section className="quality-dashboard" aria-labelledby="quality-dashboard-title">
                <div className="workspace-heading">
                  <div>
                    <p className="eyebrow">Operational dashboard</p>
                    <h2 id="quality-dashboard-title">Quality workload outlook</h2>
                    <p>
                      Tenant-scoped workload counts generated{' '}
                      {new Date(qualitySummary.generatedAt).toLocaleString()}.
                    </p>
                  </div>
                  <label>
                    Outlook
                    <select
                      value={qualityHorizonDays}
                      onChange={(event) =>
                        setQualityHorizonDays(Number(event.target.value) as 30 | 60 | 90)
                      }
                    >
                      <option value={30}>Next 30 days</option>
                      <option value={60}>Next 60 days</option>
                      <option value={90}>Next 90 days</option>
                    </select>
                  </label>
                </div>
                <div className="quality-dashboard-grid">
                  {modules.map((module) => (
                    <article key={module.kind}>
                      <strong>{module.name}</strong>
                      <span>{qualitySummary.byKind[module.kind]} total</span>
                      <dl>
                        <div>
                          <dt>Overdue</dt>
                          <dd>{qualitySummary.overdueByKind[module.kind]}</dd>
                        </div>
                        <div>
                          <dt>Due soon</dt>
                          <dd>{qualitySummary.dueSoonByKind[module.kind]}</dd>
                        </div>
                      </dl>
                    </article>
                  ))}
                </div>
              </section>
            ) : null}
            {qualitySummary && canReviewSurveyResults ? (
              <section className="workspace-panel" aria-labelledby="quality-report-title">
                <div className="workspace-heading">
                  <div>
                    <p className="eyebrow">Operational reporting</p>
                    <h2 id="quality-report-title">Quality register export</h2>
                    <p>
                      Download the organisation's current plans, schedules, internal audits and
                      surveys as a spreadsheet-safe CSV register.
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={qualityReportBusy}
                    onClick={() => void exportQualityReport()}
                  >
                    {qualityReportBusy ? 'Preparing...' : 'Export CSV'}
                  </button>
                </div>
              </section>
            ) : null}
            <PlatformAdminPanel
              accessToken={user.access_token}
              apiUrl={config.apiUrl}
              visible={Boolean(currentUser?.isPlatformAdmin)}
              tenants={platformTenants}
              newTenantName={newTenantName}
              newTenantSlug={newTenantSlug}
              newTenantAdministratorEmail={newTenantAdministratorEmail}
              busy={tenantLifecycleBusy}
              giftDurationDays={giftDurationDays}
              giftReason={giftReason}
              roleMigrationBusy={roleMigrationBusy}
              roleMigrationMessage={roleMigrationMessage}
              onNewTenantNameChange={setNewTenantName}
              onNewTenantSlugChange={setNewTenantSlug}
              onNewTenantAdministratorEmailChange={setNewTenantAdministratorEmail}
              onGiftDurationDaysChange={setGiftDurationDays}
              onGiftReasonChange={setGiftReason}
              onCreateTenant={createTenant}
              onUpdateTenantLifecycle={updateTenantLifecycle}
              onGiftSubscription={giftSubscription}
              onMigrateLegacyRoles={migrateLegacyRoles}
            />
            <BillingPanel
              visible={Boolean(tenant && canAdministerMembers)}
              tenant={tenant}
              billingData={billingData}
              billingContactName={billingContactName}
              billingEmail={billingEmail}
              billingAddress={billingAddress}
              billingBusy={billingBusy}
              onBillingContactNameChange={setBillingContactName}
              onBillingEmailChange={setBillingEmail}
              onBillingAddressChange={setBillingAddress}
              onSaveProfile={saveBillingProfile}
              onStartCheckout={startSubscriptionCheckout}
              onDownloadInvoice={downloadInvoice}
            />
            {tenant && canViewRetention ? (
              <section className="retention-panel" aria-labelledby="retention-title">
                <div className="workspace-heading">
                  <div>
                    <p className="eyebrow">Information governance</p>
                    <h2 id="retention-title">Retention and legal holds</h2>
                    <p>
                      Record the organisation&apos;s intended retention periods and preserve data
                      under a legal hold.
                    </p>
                  </div>
                  <span className="retention-status">Deletion disabled pending approval</span>
                </div>
                <p className="retention-notice">
                  These settings are governance records only. They do not delete DynamoDB records,
                  S3 objects, backups or audit evidence.
                </p>
                <div className="retention-grid">
                  <form onSubmit={(event) => void saveRetentionPolicy(event)}>
                    <h3>Retention settings</h3>
                    <div className="retention-fields">
                      <label>
                        Quality records (days)
                        <input
                          type="number"
                          min={365}
                          max={3650}
                          disabled={!canManageRetention}
                          value={retentionDraft.qualityRecordsDays}
                          onChange={(event) =>
                            updateRetentionDraft('qualityRecordsDays', Number(event.target.value))
                          }
                        />
                      </label>
                      <label>
                        Survey responses (days)
                        <input
                          type="number"
                          min={30}
                          max={2555}
                          disabled={!canManageRetention}
                          value={retentionDraft.surveyResponsesDays}
                          onChange={(event) =>
                            updateRetentionDraft('surveyResponsesDays', Number(event.target.value))
                          }
                        />
                      </label>
                      <label>
                        Files (days)
                        <input
                          type="number"
                          min={30}
                          max={3650}
                          disabled={!canManageRetention}
                          value={retentionDraft.filesDays}
                          onChange={(event) =>
                            updateRetentionDraft('filesDays', Number(event.target.value))
                          }
                        />
                      </label>
                      <label>
                        Support cases (days)
                        <input
                          type="number"
                          min={30}
                          max={2555}
                          disabled={!canManageRetention}
                          value={retentionDraft.supportCasesDays}
                          onChange={(event) =>
                            updateRetentionDraft('supportCasesDays', Number(event.target.value))
                          }
                        />
                      </label>
                      <label>
                        Audit events (days)
                        <input
                          type="number"
                          min={365}
                          max={3650}
                          disabled={!canManageRetention}
                          value={retentionDraft.auditEventsDays}
                          onChange={(event) =>
                            updateRetentionDraft('auditEventsDays', Number(event.target.value))
                          }
                        />
                      </label>
                    </div>
                    {canManageRetention ? (
                      <button disabled={retentionBusy} type="submit">
                        Save retention settings
                      </button>
                    ) : null}
                    {retentionPolicy ? (
                      <small>
                        Last updated {new Date(retentionPolicy.updatedAt).toLocaleString()}
                      </small>
                    ) : (
                      <small>No organisation-specific retention record has been saved.</small>
                    )}
                  </form>
                  <div>
                    {canManageRetention ? (
                      <form onSubmit={(event) => void createLegalHold(event)}>
                        <h3>Create legal hold</h3>
                        <label>
                          Scope
                          <select
                            value={legalHoldScope}
                            onChange={(event) =>
                              setLegalHoldScope(event.target.value as LegalHoldScope)
                            }
                          >
                            <option value="all_tenant_data">All organisation data</option>
                            <option value="quality_records">Quality records</option>
                            <option value="survey_responses">Survey responses</option>
                            <option value="files">Files</option>
                            <option value="support_cases">Support cases</option>
                            <option value="audit_events">Audit events</option>
                          </select>
                        </label>
                        <label>
                          Reason
                          <textarea
                            required
                            minLength={10}
                            maxLength={2000}
                            rows={3}
                            value={legalHoldReason}
                            onChange={(event) => setLegalHoldReason(event.target.value)}
                          />
                        </label>
                        <label>
                          Reference (optional)
                          <input
                            maxLength={200}
                            value={legalHoldReference}
                            onChange={(event) => setLegalHoldReference(event.target.value)}
                          />
                        </label>
                        <button
                          disabled={retentionBusy || legalHoldReason.trim().length < 10}
                          type="submit"
                        >
                          Create legal hold
                        </button>
                      </form>
                    ) : null}
                    <div className="legal-hold-list">
                      <h3>Legal holds</h3>
                      {legalHolds.length === 0 ? (
                        <p className="empty-state">No legal holds have been recorded.</p>
                      ) : (
                        legalHolds.map((hold) => (
                          <article key={hold.id}>
                            <strong>
                              {hold.scope.replaceAll('_', ' ')} · {hold.status}
                            </strong>
                            <small>
                              Created {new Date(hold.createdAt).toLocaleString()}
                              {hold.reference ? ` · ${hold.reference}` : ''}
                            </small>
                            <p>{hold.reason}</p>
                            {hold.status === 'active' && canManageRetention ? (
                              <>
                                <label>
                                  Reason for release
                                  <textarea
                                    minLength={10}
                                    maxLength={2000}
                                    rows={2}
                                    value={legalHoldReleaseReasons[hold.id] ?? ''}
                                    onChange={(event) =>
                                      setLegalHoldReleaseReasons((current) => ({
                                        ...current,
                                        [hold.id]: event.target.value,
                                      }))
                                    }
                                  />
                                </label>
                                <button
                                  type="button"
                                  disabled={
                                    retentionBusy ||
                                    (legalHoldReleaseReasons[hold.id]?.trim().length ?? 0) < 10
                                  }
                                  onClick={() => void releaseLegalHold(hold)}
                                >
                                  Release legal hold
                                </button>
                              </>
                            ) : null}
                          </article>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </section>
            ) : null}
            {tenant && canAdministerMembers && !currentUser?.isPlatformAdmin ? (
              <section className="offboarding-panel" aria-labelledby="offboarding-title">
                <div>
                  <p className="eyebrow">Organisation lifecycle</p>
                  <h2 id="offboarding-title">Offboarding readiness</h2>
                  <p>
                    Request a governed export and suspension process. This does not delete customer
                    data.
                  </p>
                </div>
                {tenant.offboardingStatus === 'requested' ||
                tenant.offboardingStatus === 'export_ready' ? (
                  <p>
                    Status: <strong>{tenant.offboardingStatus.replace('_', ' ')}</strong>
                  </p>
                ) : (
                  <div>
                    <label>
                      Reason for offboarding
                      <textarea
                        minLength={10}
                        maxLength={2000}
                        rows={3}
                        value={offboardingReason}
                        onChange={(event) => setOffboardingReason(event.target.value)}
                      />
                    </label>
                    <button
                      disabled={tenantLifecycleBusy || offboardingReason.trim().length < 10}
                      type="button"
                      onClick={() => void requestOffboarding()}
                    >
                      Request offboarding
                    </button>
                  </div>
                )}
              </section>
            ) : null}
            <SupportPanel
              visible={Boolean(tenant)}
              isPlatformAdmin={Boolean(currentUser?.isPlatformAdmin)}
              supportCases={supportCases}
              supportSubject={supportSubject}
              supportDescription={supportDescription}
              supportPriority={supportPriority}
              supportResponses={supportResponses}
              supportBusy={supportBusy}
              onSubjectChange={setSupportSubject}
              onDescriptionChange={setSupportDescription}
              onPriorityChange={setSupportPriority}
              onResponsesChange={setSupportResponses}
              onCreate={createSupportCase}
              onUpdate={updateSupportCase}
            />
            <WorkflowModulePanel modules={modules} onOpen={setActiveKind} />
            <section className="workspace-panel" aria-labelledby="workspace-title">
              <div className="workspace-heading">
                <div>
                  <p className="eyebrow">{activeModule.name}</p>
                  <h2 id="workspace-title">Organisation quality records</h2>
                </div>
                <span>{visibleItems.length} records</span>
              </div>
              {!user ? (
                <p className="empty-state">Log in to view and manage organisation records.</p>
              ) : !selectedTenantId ? (
                <p className="empty-state">
                  Your account is active but has not been assigned to an organisation. Contact an
                  administrator for access.
                </p>
              ) : !hasSubscriptionAccess ? (
                <p className="empty-state">
                  This organisation does not currently have an active subscription. A tenant
                  administrator can update billing details or contact quality.vet about
                  complimentary access.
                </p>
              ) : (
                <div className="workspace-grid">
                  {canManageKind(activeKind) ? (
                    <form
                      className="record-form"
                      onSubmit={(event) => void createQualityItem(event)}
                    >
                      <label>
                        Title
                        <input
                          required
                          maxLength={200}
                          value={itemTitle}
                          onChange={(event) => setItemTitle(event.target.value)}
                        />
                      </label>
                      <label>
                        Description
                        <textarea
                          maxLength={4000}
                          rows={4}
                          value={itemDescription}
                          onChange={(event) => setItemDescription(event.target.value)}
                        />
                      </label>
                      {activeKind === 'plan' ? (
                        <>
                          <label>
                            Obligation or standard
                            <input
                              required
                              maxLength={500}
                              value={planObligation}
                              onChange={(event) => setPlanObligation(event.target.value)}
                            />
                          </label>
                          <label>
                            Objective
                            <textarea
                              required
                              maxLength={2000}
                              rows={3}
                              value={planObjective}
                              onChange={(event) => setPlanObjective(event.target.value)}
                            />
                          </label>
                          <label>
                            Success measures — one per line
                            <textarea
                              required
                              maxLength={5000}
                              rows={4}
                              value={planSuccessMeasures}
                              onChange={(event) => setPlanSuccessMeasures(event.target.value)}
                            />
                          </label>
                        </>
                      ) : null}
                      {activeKind === 'schedule' ? (
                        <>
                          <label>
                            Scheduled activity
                            <input
                              required
                              maxLength={500}
                              value={scheduleActivity}
                              onChange={(event) => setScheduleActivity(event.target.value)}
                            />
                          </label>
                          <label>
                            Cadence
                            <select
                              value={scheduleCadence}
                              onChange={(event) =>
                                setScheduleCadence(event.target.value as ScheduleCadence)
                              }
                            >
                              <option value="one_off">One-off</option>
                              <option value="monthly">Monthly</option>
                              <option value="quarterly">Quarterly</option>
                              <option value="biannual">Twice yearly</option>
                              <option value="annual">Annual</option>
                            </select>
                          </label>
                        </>
                      ) : null}
                      {activeKind === 'audit' ? (
                        <>
                          <label>
                            Audit scope
                            <textarea
                              required
                              maxLength={2000}
                              rows={3}
                              value={auditScope}
                              onChange={(event) => setAuditScope(event.target.value)}
                            />
                          </label>
                          <label>
                            Audit criteria — one per line
                            <textarea
                              required
                              maxLength={8000}
                              rows={4}
                              value={auditCriteria}
                              onChange={(event) => setAuditCriteria(event.target.value)}
                            />
                          </label>
                          <label>
                            Sampling approach
                            <textarea
                              required
                              maxLength={2000}
                              rows={3}
                              value={auditSampling}
                              onChange={(event) => setAuditSampling(event.target.value)}
                            />
                          </label>
                        </>
                      ) : null}
                      {activeKind === 'survey' ? (
                        <>
                          <div className="survey-template-controls">
                            <label>
                              Start from an active template
                              <select
                                defaultValue=""
                                onChange={(event) => {
                                  applySurveyTemplate(event.target.value);
                                  event.target.value = '';
                                }}
                              >
                                <option value="">Choose a template…</option>
                                {surveyTemplates
                                  .filter((template) => template.status === 'active')
                                  .map((template) => (
                                    <option key={template.id} value={template.id}>
                                      {template.name}
                                    </option>
                                  ))}
                              </select>
                            </label>
                            {canManageKind('survey') ? (
                              <>
                                <label>
                                  Reusable template name
                                  <input
                                    maxLength={200}
                                    placeholder="e.g. Post-course learner feedback"
                                    value={surveyTemplateName}
                                    onChange={(event) => setSurveyTemplateName(event.target.value)}
                                  />
                                </label>
                                <button
                                  disabled={!surveyTemplateName.trim()}
                                  type="button"
                                  onClick={() => void createSurveyTemplate()}
                                >
                                  Save current survey fields as template
                                </button>
                              </>
                            ) : null}
                          </div>
                          {canManageKind('survey') &&
                          surveyTemplates.some((template) => template.status === 'active') ? (
                            <div className="survey-template-list">
                              {surveyTemplates
                                .filter((template) => template.status === 'active')
                                .map((template) => (
                                  <div key={template.id}>
                                    <span>{template.name}</span>
                                    <button
                                      type="button"
                                      onClick={() => void archiveSurveyTemplate(template)}
                                    >
                                      Archive
                                    </button>
                                  </div>
                                ))}
                            </div>
                          ) : null}
                          <label>
                            Stakeholder audience
                            <input
                              required
                              maxLength={500}
                              value={surveyAudience}
                              onChange={(event) => setSurveyAudience(event.target.value)}
                            />
                          </label>
                          <label>
                            Anonymous-response consent statement
                            <textarea
                              required
                              maxLength={1000}
                              rows={3}
                              value={surveyConsentStatement}
                              onChange={(event) => setSurveyConsentStatement(event.target.value)}
                            />
                          </label>
                        </>
                      ) : null}
                      <label>
                        Due date
                        <input
                          required={activeKind === 'plan' || activeKind === 'schedule'}
                          type="datetime-local"
                          value={itemDueAt}
                          onChange={(event) => setItemDueAt(event.target.value)}
                        />
                      </label>
                      {activeKind === 'survey' ? (
                        <label>
                          Questions — one per line
                          <textarea
                            required
                            maxLength={8000}
                            rows={6}
                            value={surveyQuestions}
                            onChange={(event) => setSurveyQuestions(event.target.value)}
                          />
                        </label>
                      ) : null}
                      <button disabled={savingItem} type="submit">
                        {savingItem
                          ? 'Saving…'
                          : `Create ${activeModule.name.toLowerCase()} record`}
                      </button>
                    </form>
                  ) : (
                    <p className="empty-state">
                      Your organisation role can view this workflow but cannot create or change its
                      records.
                    </p>
                  )}
                  <div className="record-list">
                    {visibleItems.length === 0 ? (
                      <p className="empty-state">No records have been created in this module.</p>
                    ) : (
                      visibleItems.map((item) => (
                        <article key={item.id}>
                          <div>
                            <strong>{item.title}</strong>
                            <select
                              aria-label={`Status for ${item.title}`}
                              disabled={
                                !canManageKind(item.kind) ||
                                availableQualityItemTransitions(item).length === 0
                              }
                              value={item.status}
                              onChange={(event) =>
                                void updateQualityItemStatus(
                                  item,
                                  event.target.value as QualityItemStatus,
                                )
                              }
                            >
                              {[item.status, ...availableQualityItemTransitions(item)].map(
                                (status) => (
                                  <option key={status} value={status}>
                                    {statusLabels[status]}
                                  </option>
                                ),
                              )}
                            </select>
                          </div>
                          {item.description ? <p>{item.description}</p> : null}
                          {(item.kind === 'plan' || item.kind === 'schedule') &&
                          item.status !== 'complete' &&
                          item.status !== 'cancelled' &&
                          canManageKind(item.kind) ? (
                            <label className="workflow-owner-control">
                              Owner
                              <select
                                value={item.ownerId}
                                onChange={(event) =>
                                  void updateQualityItem(item, { ownerId: event.target.value })
                                }
                              >
                                {!eligibleWorkflowOwners.some(
                                  (membership) => membership.userId === item.ownerId,
                                ) ? (
                                  <option value={item.ownerId}>
                                    Current owner ({item.ownerId})
                                  </option>
                                ) : null}
                                {eligibleWorkflowOwners.map((membership) => (
                                  <option key={membership.userId} value={membership.userId}>
                                    {membership.email}
                                  </option>
                                ))}
                              </select>
                            </label>
                          ) : (
                            <small>Owner {item.ownerId}</small>
                          )}
                          {item.planDetails ? (
                            <>
                              <dl className="workflow-details">
                                <div>
                                  <dt>Obligation</dt>
                                  <dd>{item.planDetails.obligation}</dd>
                                </div>
                                <div>
                                  <dt>Objective</dt>
                                  <dd>{item.planDetails.objective}</dd>
                                </div>
                                <div>
                                  <dt>Success measures</dt>
                                  <dd>{item.planDetails.successMeasures.join('; ')}</dd>
                                </div>
                              </dl>
                              <div className="workflow-control-panel">
                                <div>
                                  <strong>Plan approval</strong>
                                  <span>
                                    {
                                      planApprovalStatusLabels[
                                        item.planApproval?.status ?? 'not_submitted'
                                      ]
                                    }
                                  </span>
                                </div>
                                {item.planApproval?.comment ? (
                                  <p>{item.planApproval.comment}</p>
                                ) : null}
                                {item.status === 'draft' &&
                                item.planApproval?.status !== 'approved' &&
                                (canManageKind('plan') || canApprovePlans) ? (
                                  <>
                                    <label>
                                      Approval comment
                                      <textarea
                                        maxLength={2000}
                                        rows={2}
                                        value={planApprovalComments[item.id] ?? ''}
                                        onChange={(event) =>
                                          setPlanApprovalComments((current) => ({
                                            ...current,
                                            [item.id]: event.target.value,
                                          }))
                                        }
                                      />
                                    </label>
                                    <div className="workflow-control-actions">
                                      {(item.planApproval?.status ?? 'not_submitted') ===
                                        'not_submitted' ||
                                      item.planApproval?.status === 'changes_requested' ? (
                                        <button
                                          type="button"
                                          onClick={() => void updatePlanApproval(item, 'submit')}
                                        >
                                          Submit for approval
                                        </button>
                                      ) : null}
                                      {item.planApproval?.status === 'pending' &&
                                      canApprovePlans ? (
                                        <>
                                          <button
                                            disabled={item.createdBy === currentUser?.userId}
                                            type="button"
                                            onClick={() => void updatePlanApproval(item, 'approve')}
                                          >
                                            Approve
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() =>
                                              void updatePlanApproval(item, 'request_changes')
                                            }
                                          >
                                            Request changes
                                          </button>
                                        </>
                                      ) : null}
                                    </div>
                                    {item.planApproval?.status === 'pending' &&
                                    item.createdBy === currentUser?.userId &&
                                    canApprovePlans ? (
                                      <small>A plan creator cannot approve their own plan.</small>
                                    ) : null}
                                  </>
                                ) : null}
                              </div>
                            </>
                          ) : null}
                          {item.scheduleDetails ? (
                            <dl className="workflow-details">
                              <div>
                                <dt>Activity</dt>
                                <dd>{item.scheduleDetails.activity}</dd>
                              </div>
                              <div>
                                <dt>Cadence</dt>
                                <dd>{item.scheduleDetails.cadence.replace('_', ' ')}</dd>
                              </div>
                              <div>
                                <dt>Timezone</dt>
                                <dd>{item.scheduleDetails.timezone}</dd>
                              </div>
                            </dl>
                          ) : null}
                          {item.auditDetails ? (
                            <dl className="workflow-details">
                              <div>
                                <dt>Scope</dt>
                                <dd>{item.auditDetails.scope}</dd>
                              </div>
                              <div>
                                <dt>Criteria</dt>
                                <dd>{item.auditDetails.criteria.join('; ')}</dd>
                              </div>
                              <div>
                                <dt>Sampling</dt>
                                <dd>{item.auditDetails.sampling}</dd>
                              </div>
                            </dl>
                          ) : null}
                          {item.surveyDetails ? (
                            <dl className="workflow-details">
                              <div>
                                <dt>Audience</dt>
                                <dd>{item.surveyDetails.audience}</dd>
                              </div>
                              <div>
                                <dt>Response mode</dt>
                                <dd>Anonymous</dd>
                              </div>
                            </dl>
                          ) : null}
                          {item.dueAt ? (
                            <small>
                              {item.kind === 'schedule' ? 'Starts' : 'Due'}{' '}
                              {new Date(item.dueAt).toLocaleString()}
                            </small>
                          ) : null}
                          {item.status === 'in_progress' && canManageKind(item.kind) ? (
                            <label className="completion-evidence">
                              Completion evidence summary
                              <textarea
                                maxLength={4000}
                                rows={3}
                                value={completionSummaries[item.id] ?? ''}
                                onChange={(event) =>
                                  setCompletionSummaries((current) => ({
                                    ...current,
                                    [item.id]: event.target.value,
                                  }))
                                }
                              />
                            </label>
                          ) : null}
                          {item.completionSummary ? (
                            <p className="completion-summary">
                              <strong>Completion evidence:</strong> {item.completionSummary}
                            </p>
                          ) : null}
                          {item.kind === 'plan' && item.status === 'complete' && canApprovePlans ? (
                            <div className="workflow-control-panel">
                              <label>
                                Reason for reopening
                                <textarea
                                  required
                                  maxLength={2000}
                                  rows={2}
                                  value={planReopenReasons[item.id] ?? ''}
                                  onChange={(event) =>
                                    setPlanReopenReasons((current) => ({
                                      ...current,
                                      [item.id]: event.target.value,
                                    }))
                                  }
                                />
                              </label>
                              <button type="button" onClick={() => void reopenPlan(item)}>
                                Reopen completed plan
                              </button>
                            </div>
                          ) : null}
                          {item.kind === 'audit' ? (
                            <div className="plan-actions">
                              <button
                                type="button"
                                onClick={() => void toggleAuditFindings(item.id)}
                              >
                                {expandedAuditId === item.id ? 'Hide findings' : 'Manage findings'}
                              </button>
                              {expandedAuditId === item.id ? (
                                <div className="plan-actions-panel">
                                  <div className="plan-actions-heading">
                                    <strong>Findings and corrective actions</strong>
                                    <span>{auditFindings[item.id]?.length ?? 0} findings</span>
                                  </div>
                                  {canManageKind('audit') &&
                                  item.status !== 'complete' &&
                                  item.status !== 'cancelled' ? (
                                    <form
                                      className="plan-action-form"
                                      onSubmit={(event) => void createAuditFinding(event, item.id)}
                                    >
                                      <label>
                                        Finding title
                                        <input
                                          required
                                          maxLength={200}
                                          value={auditFindingTitle}
                                          onChange={(event) =>
                                            setAuditFindingTitle(event.target.value)
                                          }
                                        />
                                      </label>
                                      <label>
                                        Classification
                                        <select
                                          value={auditFindingClassification}
                                          onChange={(event) =>
                                            setAuditFindingClassification(
                                              event.target.value as AuditFindingClassification,
                                            )
                                          }
                                        >
                                          {Object.entries(auditFindingClassificationLabels).map(
                                            ([value, label]) => (
                                              <option key={value} value={value}>
                                                {label}
                                              </option>
                                            ),
                                          )}
                                        </select>
                                      </label>
                                      <label>
                                        Finding statement
                                        <textarea
                                          required
                                          maxLength={4000}
                                          rows={3}
                                          value={auditFindingStatement}
                                          onChange={(event) =>
                                            setAuditFindingStatement(event.target.value)
                                          }
                                        />
                                      </label>
                                      <label>
                                        Corrective action
                                        <textarea
                                          required={
                                            auditFindingClassification === 'minor_nonconformity' ||
                                            auditFindingClassification === 'major_nonconformity'
                                          }
                                          maxLength={4000}
                                          rows={3}
                                          value={auditFindingCorrectiveAction}
                                          onChange={(event) =>
                                            setAuditFindingCorrectiveAction(event.target.value)
                                          }
                                        />
                                      </label>
                                      <label>
                                        Due date
                                        <input
                                          type="datetime-local"
                                          value={auditFindingDueAt}
                                          onChange={(event) =>
                                            setAuditFindingDueAt(event.target.value)
                                          }
                                        />
                                      </label>
                                      <label>
                                        Finding evidence
                                        <select
                                          value={auditFindingEvidenceFileId}
                                          onChange={(event) =>
                                            setAuditFindingEvidenceFileId(event.target.value)
                                          }
                                        >
                                          <option value="">No file linked yet</option>
                                          {files
                                            .filter((file) => file.status === 'available')
                                            .map((file) => (
                                              <option key={file.id} value={file.id}>
                                                {file.fileName}
                                              </option>
                                            ))}
                                        </select>
                                      </label>
                                      <button type="submit">Add finding</button>
                                    </form>
                                  ) : null}
                                  <div className="plan-action-list">
                                    {(auditFindings[item.id] ?? []).length === 0 ? (
                                      <p className="empty-state">No findings have been recorded.</p>
                                    ) : (
                                      (auditFindings[item.id] ?? []).map((finding) => (
                                        <article key={finding.id}>
                                          <div className="plan-action-row">
                                            <div>
                                              <strong>{finding.title}</strong>
                                              <small>
                                                {
                                                  auditFindingClassificationLabels[
                                                    finding.classification
                                                  ]
                                                }
                                              </small>
                                            </div>
                                            <select
                                              aria-label={`Status for ${finding.title}`}
                                              disabled={
                                                !canManageKind('audit') ||
                                                permittedAuditFindingTransitions[finding.status]
                                                  .length === 0 ||
                                                item.status === 'complete' ||
                                                item.status === 'cancelled'
                                              }
                                              value={finding.status}
                                              onChange={(event) =>
                                                void updateAuditFindingStatus(
                                                  finding,
                                                  event.target.value as AuditFindingStatus,
                                                )
                                              }
                                            >
                                              {[
                                                finding.status,
                                                ...permittedAuditFindingTransitions[finding.status],
                                              ].map((status) => (
                                                <option key={status} value={status}>
                                                  {auditFindingStatusLabels[status]}
                                                </option>
                                              ))}
                                            </select>
                                          </div>
                                          <p>{finding.statement}</p>
                                          {finding.correctiveAction ? (
                                            <p>
                                              <strong>Corrective action:</strong>{' '}
                                              {finding.correctiveAction}
                                            </p>
                                          ) : null}
                                          <small>
                                            Owner {finding.ownerId}
                                            {finding.dueAt
                                              ? ` · due ${new Date(finding.dueAt).toLocaleString()}`
                                              : ''}
                                          </small>
                                          {finding.status !== 'closed' && canManageKind('audit') ? (
                                            <>
                                              <label className="completion-evidence">
                                                Closure evidence summary
                                                <textarea
                                                  maxLength={4000}
                                                  rows={3}
                                                  value={
                                                    auditFindingClosureSummaries[finding.id] ?? ''
                                                  }
                                                  onChange={(event) =>
                                                    setAuditFindingClosureSummaries((current) => ({
                                                      ...current,
                                                      [finding.id]: event.target.value,
                                                    }))
                                                  }
                                                />
                                              </label>
                                              <label>
                                                Closure evidence file
                                                <select
                                                  value={
                                                    auditFindingClosureEvidence[finding.id] ?? ''
                                                  }
                                                  onChange={(event) =>
                                                    setAuditFindingClosureEvidence((current) => ({
                                                      ...current,
                                                      [finding.id]: event.target.value,
                                                    }))
                                                  }
                                                >
                                                  <option value="">No closure file selected</option>
                                                  {files
                                                    .filter(
                                                      (file) =>
                                                        file.status === 'available' &&
                                                        !finding.closureEvidenceFileIds.includes(
                                                          file.id,
                                                        ),
                                                    )
                                                    .map((file) => (
                                                      <option key={file.id} value={file.id}>
                                                        {file.fileName}
                                                      </option>
                                                    ))}
                                                </select>
                                              </label>
                                            </>
                                          ) : null}
                                          {finding.closureSummary ? (
                                            <p className="completion-summary">
                                              <strong>Closure evidence:</strong>{' '}
                                              {finding.closureSummary}
                                            </p>
                                          ) : null}
                                          <div className="plan-action-evidence">
                                            {[
                                              ...new Set([
                                                ...finding.evidenceFileIds,
                                                ...finding.closureEvidenceFileIds,
                                              ]),
                                            ].map((fileId) => {
                                              const file = files.find(
                                                (candidate) => candidate.id === fileId,
                                              );
                                              return file ? (
                                                <button
                                                  key={file.id}
                                                  type="button"
                                                  onClick={() => void downloadFile(file)}
                                                >
                                                  {file.fileName}
                                                </button>
                                              ) : (
                                                <span key={fileId}>Evidence unavailable</span>
                                              );
                                            })}
                                            {canManageKind('audit') &&
                                            finding.status !== 'closed' ? (
                                              <select
                                                aria-label={`Link evidence to ${finding.title}`}
                                                value=""
                                                onChange={(event) =>
                                                  void linkAuditFindingEvidence(
                                                    finding,
                                                    event.target.value,
                                                  )
                                                }
                                              >
                                                <option value="">
                                                  Link finding evidence file…
                                                </option>
                                                {files
                                                  .filter(
                                                    (file) =>
                                                      file.status === 'available' &&
                                                      !finding.evidenceFileIds.includes(file.id),
                                                  )
                                                  .map((file) => (
                                                    <option key={file.id} value={file.id}>
                                                      {file.fileName}
                                                    </option>
                                                  ))}
                                              </select>
                                            ) : null}
                                          </div>
                                        </article>
                                      ))
                                    )}
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                          {item.kind === 'schedule' ? (
                            <div className="schedule-occurrences">
                              <button
                                type="button"
                                onClick={() => void toggleScheduleOccurrences(item.id)}
                              >
                                {expandedScheduleId === item.id
                                  ? 'Hide occurrences'
                                  : 'Manage occurrences'}
                              </button>
                              {expandedScheduleId === item.id ? (
                                <div className="schedule-occurrences-panel">
                                  <div className="schedule-occurrences-heading">
                                    <div>
                                      <strong>Scheduled occurrences</strong>
                                      <span>
                                        {scheduleOccurrences[item.id]?.length ?? 0} occurrences
                                      </span>
                                    </div>
                                    <div className="view-toggle">
                                      <button
                                        className={
                                          scheduleOccurrenceView === 'agenda' ? 'selected' : ''
                                        }
                                        type="button"
                                        onClick={() => setScheduleOccurrenceView('agenda')}
                                      >
                                        Agenda
                                      </button>
                                      <button
                                        className={
                                          scheduleOccurrenceView === 'calendar' ? 'selected' : ''
                                        }
                                        type="button"
                                        onClick={() => setScheduleOccurrenceView('calendar')}
                                      >
                                        Calendar
                                      </button>
                                    </div>
                                  </div>
                                  {(scheduleOccurrences[item.id] ?? []).length === 0 ? (
                                    <p className="empty-state">
                                      No generated occurrences are available for this schedule.
                                    </p>
                                  ) : (
                                    groupScheduleOccurrences(
                                      scheduleOccurrences[item.id] ?? [],
                                      scheduleOccurrenceView,
                                    ).map((group) => (
                                      <div className="schedule-occurrence-group" key={group.label}>
                                        {group.label ? <h4>{group.label}</h4> : null}
                                        {group.items.map((occurrence) => (
                                          <article
                                            className={
                                              isOccurrenceOverdue(occurrence) ? 'overdue' : ''
                                            }
                                            key={occurrence.id}
                                          >
                                            <div className="schedule-occurrence-row">
                                              <div>
                                                <strong>
                                                  Occurrence {occurrence.sequence}
                                                  {isOccurrenceOverdue(occurrence) ? (
                                                    <span className="overdue-label">Overdue</span>
                                                  ) : null}
                                                </strong>
                                                <small>
                                                  {new Date(occurrence.scheduledFor).toLocaleString(
                                                    'en-AU',
                                                    {
                                                      timeZone: 'Australia/Sydney',
                                                    },
                                                  )}{' '}
                                                  · Australia/Sydney
                                                </small>
                                              </div>
                                              <select
                                                aria-label={`Status for occurrence ${occurrence.sequence}`}
                                                disabled={
                                                  !canManageKind('schedule') ||
                                                  permittedScheduleOccurrenceTransitions[
                                                    occurrence.status
                                                  ].length === 0 ||
                                                  item.status === 'complete' ||
                                                  item.status === 'cancelled'
                                                }
                                                value={occurrence.status}
                                                onChange={(event) =>
                                                  void updateScheduleOccurrenceStatus(
                                                    occurrence,
                                                    event.target.value as ScheduleOccurrenceStatus,
                                                  )
                                                }
                                              >
                                                {[
                                                  occurrence.status,
                                                  ...permittedScheduleOccurrenceTransitions[
                                                    occurrence.status
                                                  ].filter((status) => status !== 'skipped'),
                                                ].map((status) => (
                                                  <option key={status} value={status}>
                                                    {scheduleOccurrenceStatusLabels[status]}
                                                  </option>
                                                ))}
                                              </select>
                                            </div>
                                            {occurrence.status !== 'complete' &&
                                            occurrence.status !== 'skipped' &&
                                            canManageKind('schedule') ? (
                                              <label className="workflow-owner-control">
                                                Occurrence owner
                                                <select
                                                  value={occurrence.ownerId}
                                                  onChange={(event) =>
                                                    void updateScheduleOccurrence(occurrence, {
                                                      ownerId: event.target.value,
                                                    })
                                                  }
                                                >
                                                  {!eligibleWorkflowOwners.some(
                                                    (membership) =>
                                                      membership.userId === occurrence.ownerId,
                                                  ) ? (
                                                    <option value={occurrence.ownerId}>
                                                      Current owner ({occurrence.ownerId})
                                                    </option>
                                                  ) : null}
                                                  {eligibleWorkflowOwners.map((membership) => (
                                                    <option
                                                      key={membership.userId}
                                                      value={membership.userId}
                                                    >
                                                      {membership.email}
                                                    </option>
                                                  ))}
                                                </select>
                                              </label>
                                            ) : (
                                              <small>Owner {occurrence.ownerId}</small>
                                            )}
                                            {occurrence.status === 'pending' &&
                                            canManageKind('schedule') ? (
                                              <div className="workflow-control-panel">
                                                <strong>Reschedule occurrence</strong>
                                                <label>
                                                  New Sydney date and time
                                                  <input
                                                    type="datetime-local"
                                                    value={
                                                      scheduleOccurrenceReschedules[occurrence.id]
                                                        ?.scheduledFor ?? ''
                                                    }
                                                    onChange={(event) =>
                                                      setScheduleOccurrenceReschedules(
                                                        (current) => ({
                                                          ...current,
                                                          [occurrence.id]: {
                                                            scheduledFor: event.target.value,
                                                            reason:
                                                              current[occurrence.id]?.reason ?? '',
                                                          },
                                                        }),
                                                      )
                                                    }
                                                  />
                                                </label>
                                                <label>
                                                  Reschedule reason
                                                  <textarea
                                                    maxLength={2000}
                                                    rows={2}
                                                    value={
                                                      scheduleOccurrenceReschedules[occurrence.id]
                                                        ?.reason ?? ''
                                                    }
                                                    onChange={(event) =>
                                                      setScheduleOccurrenceReschedules(
                                                        (current) => ({
                                                          ...current,
                                                          [occurrence.id]: {
                                                            scheduledFor:
                                                              current[occurrence.id]
                                                                ?.scheduledFor ?? '',
                                                            reason: event.target.value,
                                                          },
                                                        }),
                                                      )
                                                    }
                                                  />
                                                </label>
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    void rescheduleOccurrence(occurrence)
                                                  }
                                                >
                                                  Reschedule
                                                </button>
                                              </div>
                                            ) : null}
                                            {(occurrence.status === 'pending' ||
                                              occurrence.status === 'in_progress') &&
                                            canManageKind('schedule') ? (
                                              <div className="workflow-control-panel">
                                                <label className="completion-evidence">
                                                  Resolution evidence summary
                                                  <textarea
                                                    maxLength={4000}
                                                    rows={3}
                                                    value={
                                                      scheduleOccurrenceSummaries[occurrence.id] ??
                                                      ''
                                                    }
                                                    onChange={(event) =>
                                                      setScheduleOccurrenceSummaries((current) => ({
                                                        ...current,
                                                        [occurrence.id]: event.target.value,
                                                      }))
                                                    }
                                                  />
                                                </label>
                                                <label>
                                                  Skip reason
                                                  <select
                                                    value={
                                                      scheduleOccurrenceSkipReasons[
                                                        occurrence.id
                                                      ] ?? ''
                                                    }
                                                    onChange={(event) =>
                                                      setScheduleOccurrenceSkipReasons(
                                                        (current) => ({
                                                          ...current,
                                                          [occurrence.id]: event.target
                                                            .value as ScheduleOccurrenceSkipReason,
                                                        }),
                                                      )
                                                    }
                                                  >
                                                    <option value="">Select a reason</option>
                                                    {Object.entries(
                                                      scheduleOccurrenceSkipReasonLabels,
                                                    ).map(([value, label]) => (
                                                      <option key={value} value={value}>
                                                        {label}
                                                      </option>
                                                    ))}
                                                  </select>
                                                </label>
                                                {scheduleOccurrenceSkipReasons[occurrence.id] ===
                                                'other' ? (
                                                  <label>
                                                    Other skip reason details
                                                    <textarea
                                                      maxLength={2000}
                                                      rows={2}
                                                      value={
                                                        scheduleOccurrenceSkipDetails[
                                                          occurrence.id
                                                        ] ?? ''
                                                      }
                                                      onChange={(event) =>
                                                        setScheduleOccurrenceSkipDetails(
                                                          (current) => ({
                                                            ...current,
                                                            [occurrence.id]: event.target.value,
                                                          }),
                                                        )
                                                      }
                                                    />
                                                  </label>
                                                ) : null}
                                                <button
                                                  type="button"
                                                  onClick={() => void skipOccurrence(occurrence)}
                                                >
                                                  Skip occurrence
                                                </button>
                                              </div>
                                            ) : null}
                                            {occurrence.resolutionSummary ? (
                                              <p className="completion-summary">
                                                <strong>Resolution evidence:</strong>{' '}
                                                {occurrence.resolutionSummary}
                                              </p>
                                            ) : null}
                                            {occurrence.skipReason ? (
                                              <p className="completion-summary">
                                                <strong>Skip reason:</strong>{' '}
                                                {
                                                  scheduleOccurrenceSkipReasonLabels[
                                                    occurrence.skipReason
                                                  ]
                                                }
                                                {occurrence.skipReasonDetails
                                                  ? ` — ${occurrence.skipReasonDetails}`
                                                  : ''}
                                              </p>
                                            ) : null}
                                            <div className="schedule-occurrence-evidence">
                                              {occurrence.evidenceFileIds.map((fileId) => {
                                                const file = files.find(
                                                  (candidate) => candidate.id === fileId,
                                                );
                                                return file ? (
                                                  <button
                                                    key={file.id}
                                                    type="button"
                                                    onClick={() => void downloadFile(file)}
                                                  >
                                                    {file.fileName}
                                                  </button>
                                                ) : (
                                                  <span key={fileId}>Evidence unavailable</span>
                                                );
                                              })}
                                              {canManageKind('schedule') &&
                                              occurrence.status !== 'complete' &&
                                              occurrence.status !== 'skipped' ? (
                                                <select
                                                  aria-label={`Link evidence to occurrence ${occurrence.sequence}`}
                                                  value=""
                                                  onChange={(event) =>
                                                    void linkScheduleOccurrenceEvidence(
                                                      occurrence,
                                                      event.target.value,
                                                    )
                                                  }
                                                >
                                                  <option value="">Link evidence file…</option>
                                                  {files
                                                    .filter(
                                                      (file) =>
                                                        file.status === 'available' &&
                                                        !occurrence.evidenceFileIds.includes(
                                                          file.id,
                                                        ),
                                                    )
                                                    .map((file) => (
                                                      <option key={file.id} value={file.id}>
                                                        {file.fileName}
                                                      </option>
                                                    ))}
                                                </select>
                                              ) : null}
                                            </div>
                                          </article>
                                        ))}
                                      </div>
                                    ))
                                  )}
                                  <p className="reminder-note">
                                    Automated reminders are not enabled. Reminder timing and
                                    recipient rules require operational approval before delivery is
                                    activated.
                                  </p>
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                          {item.kind === 'plan' ? (
                            <div className="plan-actions">
                              <button type="button" onClick={() => void togglePlanActions(item.id)}>
                                {expandedPlanId === item.id ? 'Hide actions' : 'Manage actions'}
                              </button>
                              {expandedPlanId === item.id ? (
                                <div className="plan-actions-panel">
                                  <div className="plan-actions-heading">
                                    <strong>Improvement actions</strong>
                                    <span>{planActions[item.id]?.length ?? 0} actions</span>
                                  </div>
                                  {canManageKind('plan') &&
                                  item.status !== 'complete' &&
                                  item.status !== 'cancelled' ? (
                                    <form
                                      className="plan-action-form"
                                      onSubmit={(event) => void createPlanAction(event, item.id)}
                                    >
                                      <label>
                                        Action required
                                        <textarea
                                          required
                                          maxLength={2000}
                                          rows={3}
                                          value={planActionDescription}
                                          onChange={(event) =>
                                            setPlanActionDescription(event.target.value)
                                          }
                                        />
                                      </label>
                                      <label>
                                        Due date
                                        <input
                                          type="datetime-local"
                                          value={planActionDueAt}
                                          onChange={(event) =>
                                            setPlanActionDueAt(event.target.value)
                                          }
                                        />
                                      </label>
                                      <label>
                                        Initial evidence
                                        <select
                                          value={planActionEvidenceFileId}
                                          onChange={(event) =>
                                            setPlanActionEvidenceFileId(event.target.value)
                                          }
                                        >
                                          <option value="">No file linked yet</option>
                                          {files
                                            .filter((file) => file.status === 'available')
                                            .map((file) => (
                                              <option key={file.id} value={file.id}>
                                                {file.fileName}
                                              </option>
                                            ))}
                                        </select>
                                      </label>
                                      <button type="submit">Add action</button>
                                    </form>
                                  ) : null}
                                  <div className="plan-action-list">
                                    {(planActions[item.id] ?? []).length === 0 ? (
                                      <p className="empty-state">No actions have been added.</p>
                                    ) : (
                                      (planActions[item.id] ?? []).map((action) => (
                                        <article key={action.id}>
                                          <div className="plan-action-row">
                                            <strong>{action.description}</strong>
                                            <select
                                              aria-label={`Status for ${action.description}`}
                                              disabled={
                                                !canManageKind('plan') ||
                                                permittedPlanActionTransitions[action.status]
                                                  .length === 0
                                              }
                                              value={action.status}
                                              onChange={(event) =>
                                                void updatePlanActionStatus(
                                                  action,
                                                  event.target.value as PlanActionStatus,
                                                )
                                              }
                                            >
                                              {[
                                                action.status,
                                                ...permittedPlanActionTransitions[action.status],
                                              ].map((status) => (
                                                <option key={status} value={status}>
                                                  {planActionStatusLabels[status]}
                                                </option>
                                              ))}
                                            </select>
                                          </div>
                                          {action.status !== 'complete' &&
                                          action.status !== 'cancelled' &&
                                          canManageKind('plan') ? (
                                            <label className="workflow-owner-control">
                                              Action owner
                                              <select
                                                value={action.ownerId}
                                                onChange={(event) =>
                                                  void updatePlanAction(action, {
                                                    ownerId: event.target.value,
                                                  })
                                                }
                                              >
                                                {!eligibleWorkflowOwners.some(
                                                  (membership) =>
                                                    membership.userId === action.ownerId,
                                                ) ? (
                                                  <option value={action.ownerId}>
                                                    Current owner ({action.ownerId})
                                                  </option>
                                                ) : null}
                                                {eligibleWorkflowOwners.map((membership) => (
                                                  <option
                                                    key={membership.userId}
                                                    value={membership.userId}
                                                  >
                                                    {membership.email}
                                                  </option>
                                                ))}
                                              </select>
                                              {action.dueAt
                                                ? `Due ${new Date(action.dueAt).toLocaleString()}`
                                                : ''}
                                            </label>
                                          ) : (
                                            <small>
                                              Owner {action.ownerId}
                                              {action.dueAt
                                                ? ` · due ${new Date(action.dueAt).toLocaleString()}`
                                                : ''}
                                            </small>
                                          )}
                                          {action.status === 'in_progress' &&
                                          canManageKind('plan') ? (
                                            <label className="completion-evidence">
                                              Completion evidence summary
                                              <textarea
                                                maxLength={4000}
                                                rows={3}
                                                value={
                                                  planActionCompletionSummaries[action.id] ?? ''
                                                }
                                                onChange={(event) =>
                                                  setPlanActionCompletionSummaries((current) => ({
                                                    ...current,
                                                    [action.id]: event.target.value,
                                                  }))
                                                }
                                              />
                                            </label>
                                          ) : null}
                                          {action.completionSummary ? (
                                            <p className="completion-summary">
                                              <strong>Completion evidence:</strong>{' '}
                                              {action.completionSummary}
                                            </p>
                                          ) : null}
                                          <div className="plan-action-evidence">
                                            {action.evidenceFileIds.map((fileId) => {
                                              const file = files.find(
                                                (candidate) => candidate.id === fileId,
                                              );
                                              return file ? (
                                                <button
                                                  key={file.id}
                                                  type="button"
                                                  onClick={() => void downloadFile(file)}
                                                >
                                                  {file.fileName}
                                                </button>
                                              ) : (
                                                <span key={fileId}>Evidence unavailable</span>
                                              );
                                            })}
                                            {canManageKind('plan') &&
                                            action.status !== 'complete' &&
                                            action.status !== 'cancelled' ? (
                                              <select
                                                aria-label={`Link evidence to ${action.description}`}
                                                value=""
                                                onChange={(event) =>
                                                  void linkPlanActionEvidence(
                                                    action,
                                                    event.target.value,
                                                  )
                                                }
                                              >
                                                <option value="">Link evidence file…</option>
                                                {files
                                                  .filter(
                                                    (file) =>
                                                      file.status === 'available' &&
                                                      !action.evidenceFileIds.includes(file.id),
                                                  )
                                                  .map((file) => (
                                                    <option key={file.id} value={file.id}>
                                                      {file.fileName}
                                                    </option>
                                                  ))}
                                              </select>
                                            ) : null}
                                          </div>
                                        </article>
                                      ))
                                    )}
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                          {item.kind === 'survey' ? (
                            <div className="survey-actions">
                              {canManageKind('survey') ? (
                                <>
                                  <button type="button" onClick={() => void shareSurvey(item)}>
                                    Create stakeholder link
                                  </button>
                                  <button type="button" onClick={() => void loadSurveyLinks(item)}>
                                    Manage links
                                  </button>
                                </>
                              ) : null}
                              {canReviewSurveyResults ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => void loadSurveyResults(item)}
                                  >
                                    View responses
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => void exportSurveyResponses(item)}
                                  >
                                    Export CSV
                                  </button>
                                </>
                              ) : null}
                              {surveyLinks[item.id] !== undefined ? (
                                <div className="survey-link-register">
                                  <strong>Stakeholder links</strong>
                                  {surveyLinks[item.id]?.length === 0 ? (
                                    <p className="empty-state">
                                      No managed links have been created.
                                    </p>
                                  ) : (
                                    surveyLinks[item.id]?.map((token) => {
                                      const expired =
                                        new Date(token.expiresAt).getTime() <= Date.now();
                                      const exhausted =
                                        token.responseCount >= token.maximumResponses;
                                      const displayStatus =
                                        token.status === 'revoked'
                                          ? 'revoked'
                                          : expired
                                            ? 'expired'
                                            : exhausted
                                              ? 'response limit reached'
                                              : 'active';
                                      return (
                                        <article key={token.tokenHash}>
                                          <div>
                                            <strong>
                                              Link …{token.tokenHash.slice(-8)} · {displayStatus}
                                            </strong>
                                            <small>
                                              {token.responseCount}/{token.maximumResponses}{' '}
                                              responses · expires{' '}
                                              {new Date(token.expiresAt).toLocaleString()}
                                            </small>
                                          </div>
                                          {token.status === 'active' && !expired && !exhausted ? (
                                            <button
                                              type="button"
                                              onClick={() => void revokeSurveyLink(item, token)}
                                            >
                                              Revoke
                                            </button>
                                          ) : null}
                                        </article>
                                      );
                                    })
                                  )}
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                        </article>
                      ))
                    )}
                  </div>
                </div>
              )}
              {surveyShareLink ? (
                <div className="share-link">
                  <strong>Survey link created</strong>
                  <a href={surveyShareLink} target="_blank" rel="noreferrer">
                    {surveyShareLink}
                  </a>
                  <button
                    type="button"
                    onClick={() => void navigator.clipboard.writeText(surveyShareLink)}
                  >
                    Copy link
                  </button>
                  <form onSubmit={(event) => void sendSurveyInvitation(event)}>
                    <input
                      aria-label="Survey recipient email"
                      required
                      type="email"
                      placeholder="recipient@example.com"
                      value={notificationEmail}
                      onChange={(event) => setNotificationEmail(event.target.value)}
                    />
                    <button type="submit">Send invitation</button>
                  </form>
                </div>
              ) : null}
              {surveyResults ? (
                <div className="survey-results">
                  <div>
                    <strong>{surveyResults.title}</strong>
                    <span>{surveyResults.items.length} responses</span>
                  </div>
                  {surveyResults.items.length === 0 ? (
                    <p className="empty-state">No responses have been submitted.</p>
                  ) : (
                    surveyResults.items.map((result) => (
                      <article key={result.id}>
                        <small>
                          Submitted {new Date(result.submittedAt).toLocaleString()}
                          {result.consentedAt
                            ? ` · consent recorded ${new Date(result.consentedAt).toLocaleString()}`
                            : ' · legacy response'}
                        </small>
                        {surveyResults.questions.map((question, index) => (
                          <div key={`${result.id}-${index}`}>
                            <strong>{question}</strong>
                            <p>{result.answers[index] ?? ''}</p>
                          </div>
                        ))}
                      </article>
                    ))
                  )}
                </div>
              ) : null}
            </section>
            <FilesPanel
              visible={Boolean(user && selectedTenantId && hasSubscriptionAccess)}
              files={files}
              selectedFile={selectedFile}
              uploadingFile={uploadingFile}
              setSelectedFile={setSelectedFile}
              onUpload={uploadFile}
              onDownload={downloadFile}
              onDelete={deleteFile}
            />
            <OrganisationStructurePanel
              visible={Boolean(tenant && canAdministerMembers)}
              locations={tenant?.locations ?? []}
              teams={tenant?.teams ?? []}
              busy={organisationStructureBusy}
              onSave={saveOrganisationStructure}
            />
            <TenantMembersPanel
              visible={canManagePersonnel}
              canAdminister={canAdministerMembers}
              canManageAllAssignments={canManageAllPersonnelAssignments}
              currentUserId={currentUser?.userId ?? String(user.profile.sub)}
              ledTeamIds={selectedMembership?.teamLeaderTeamIds ?? []}
              members={members}
              organisationName={selectedOrganisationName || 'Current organisation'}
              {...(canAdministerMembers ? { securitySummaries: memberSecuritySummaries } : {})}
              locations={tenant?.locations ?? []}
              teams={tenant?.teams ?? []}
              inviteEmail={inviteEmail}
              inviteRole={inviteRole}
              onInviteEmailChange={setInviteEmail}
              onInviteRoleChange={setInviteRole}
              onInvite={inviteMember}
              onUpdateMembership={updateMembership}
              onResetMfa={resetMemberMfa}
            />
            <AuditHistoryPanel
              events={auditEvents}
              onExport={exportAuditEvents}
              visible={Boolean(currentUser?.isPlatformAdmin)}
            />
            <PlatformStatusPanel apiUrl={config.apiUrl} />
          </>
        ) : null}
      </main>
      {currentUser?.userId ? (
        <AnnouncementModalLayer
          accessToken={user.access_token}
          apiUrl={config.apiUrl}
          userId={currentUser.userId}
        />
      ) : null}
      <FloatingActions
        submitterName={[profile.firstName, profile.familyName].filter(Boolean).join(' ')}
      />
    </div>
  );
}
